/**
 * Campaign contracts — slice 5, "send a campaign".
 *
 * Everything before this slice was authoring. This is the first file whose
 * shapes decide whether a message reaches a stranger's inbox, and the first that
 * can damage a customer's own AWS account. Three rules shape every schema below.
 *
 * **1. THE SEND GUARD, and it is first because everything else depends on it.**
 * This database holds 1,287 real contacts from a real summit mailing list.
 * `SEND_ALLOWLIST` is a list of addresses; an address that is not on it never
 * becomes a message — it becomes a `skipped` recipient carrying a reason a
 * person can read. **An empty allow-list sends nothing at all.** The guard fails
 * closed, because the failure mode of failing open is mailing a thousand banks.
 * See {@link evaluateSendGuard}, which is the one implementation of that rule,
 * and {@link sendGuardStatusSchema}, whose `enforced` field is
 * `z.literal(true)` — **the contract cannot express a disabled guard**, so
 * turning it off is a contract change with a review rather than a config flip.
 *
 * The guard runs **twice**: once when the recipient list is materialised, and
 * again immediately before the provider call. A materialised list outlives the
 * config that produced it, and the second check is the one that holds when a
 * campaign is scheduled today and sends tomorrow. {@link SendGuardStage} exists
 * so a decision can say which of the two refused, and so a reviewer can grep for
 * both call sites.
 *
 * **2. A campaign reaches the channel-reachable count, not the segment size.**
 * Slice 2 settled this against real data: a segment of 1,278 unique recipients
 * reaches **526 by email** and 752 by WhatsApp, and neither channel touches the
 * other group. {@link campaignReach} is the only supported way to get that
 * number, so no screen can accidentally put the segment total where the reach
 * belongs — SLICES names showing the segment total there as *the* failure mode,
 * because it lets someone schedule an email campaign believing they are reaching
 * 1,278 people and actually reach 526.
 *
 * **3. De-duplication is a constraint AND a rule.** `campaign_recipients` is
 * unique on `(campaign_id, contact_id)` and on `(campaign_id, dedupe_key)`, and
 * {@link dedupeKeyForContact} derives that key — `coalesce(email, phone_e164)`,
 * normalised — so one address can never get two rows. That constraint cannot see
 * a person: since slice 58 the unit a campaign reaches once is the PERSON
 * (`contacts.person_id`), and keeping one person to one row is materialisation's
 * per-person walk, not an index — two rows of one person carry different keys.
 * `uniqueRecipients` counts persons; materialisation writes at most one row per
 * person (exactly one on email). If the count and the send ever disagree, a
 * segment promises a number the send then misses, which is the exact failure
 * slice 2 exists to prevent.
 *
 * Credits (`paused_insufficient_credits`), delivery webhooks (`delivered`,
 * `bounced`) and WhatsApp are slice 6 and 7. Their enum members are referenced
 * here because the enums are fixed in CONTRACT.md, and the shapes below carry
 * them so those slices are additive — but nothing in slice 5 can produce one.
 */
import { z } from 'zod';

import {
  emailSchema,
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  phoneSchema,
  sortDirectionSchema,
  uuidSchema,
} from '../common.js';
import {
  campaignStatusSchema,
  channelSchema,
  messageStatusSchema,
  recipientStatusSchema,
  suppressionReasonSchema,
  templateStatusSchema,
  templateTypeSchema,
  type CampaignStatus,
  type Channel,
  type TemplateStatus,
  type TemplateType,
} from '../enums.js';
import type { Contact } from './contact.js';
import { sesTransportSchema, type MetaReadiness, type SesReadinessState } from './integration.js';
import { PRESSURE_CRITICAL_AT, PRESSURE_WINDOW_DAYS } from './pressure.js';
import type { SegmentCounts } from './segment.js';
// A value import, and a new edge — but a leaf-ward one. `template.js` imports
// `common`, `enums`, `contact` and `whatsapp`, and none of those reaches back
// here, so there is no cycle to guard with `z.lazy()`. It is imported for the
// sentence the image refusal below prints, which the template editor prints too:
// one function, so the campaign screen and the editor cannot give a person two
// different accounts of how to fix the same picture.
import {
  templateAttachmentsUnpublishedMessage,
  templateImagesUnpublishedMessage,
  type TemplateAttachmentDelivery,
  type TemplateImageDelivery,
} from './template.js';
// Slice 49, and a leaf-ward edge for the same reason the `template.js` one
// above is: `whatsapp.js` is in a cycle with `template.js` and neither reaches
// back here, so a campaign contract may import from it but nothing in it may
// import a campaign shape. Values, not just types: `waBindingListSchema` is the
// ONE array shape create, update and preview bind through, and
// `formatWaPositions` is the one printer the schedule blocker and the send
// refusal share — two renderings of "{{1}} and {{3}}" would be two sentences a
// person compares against one template. The web panel prints one token at a
// time and never a list, so it is not a third caller, whatever this comment
// said before slice 49's review counted them.
import {
  formatWaPositions,
  waBindingListSchema,
  waBindingState,
  waMediaFormatSchema,
  waPositionSchema,
  waVariableBindingSchema,
  type WaVariableBinding,
} from './whatsapp.js';

/* -------------------------------------------------------------------------- */
/* Limits                                                                      */
/* -------------------------------------------------------------------------- */

export const MAX_CAMPAIGN_NAME_LENGTH = 120;

/**
 * How far in the past a `scheduledAt` may be and still be accepted.
 *
 * Not zero, because the browser's clock and the server's clock disagree by
 * seconds and "schedule for now" is a thing a person does by pressing a button
 * that filled the field a moment ago. Five minutes absorbs skew without letting
 * anyone schedule yesterday.
 */
export const SCHEDULE_PAST_GRACE_MINUTES = 5;

/**
 * Quiet hours, in the campaign's own timezone: 21:00 to 08:00.
 *
 * **These warn, they never block.** Marketing mail at 03:00 generates
 * complaints and complaints are what get an AWS account suspended — but it is
 * the tenant's call, and the postal-address lesson from slice 4 applies: a rule
 * that refuses the common case to prevent the rare one gets worked around
 * rather than heeded.
 */
export const QUIET_HOURS_START_HOUR = 21;
export const QUIET_HOURS_END_HOUR = 8;

/* -------------------------------------------------------------------------- */
/* Timezone                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Whether the platform recognises an IANA timezone name.
 *
 * `Intl` is a platform global, not a dependency, so this keeps `@wend/shared`'s
 * "no runtime deps beyond zod" promise while still asking the only authority
 * that matters — the same one that will resolve the zone when the scheduler
 * converts a local time to an instant.
 *
 * It fails **open** when the platform cannot answer, which is the opposite of
 * how the send guard fails and is deliberate: this is a formatting question, not
 * a safety one, and an ICU-less runtime refusing `Asia/Kolkata` would make the
 * product unusable rather than safe.
 */
export function isKnownTimeZone(timezone: string): boolean {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return true;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * An IANA timezone name, e.g. `Asia/Kolkata`, `Europe/London`, `UTC`.
 *
 * Shape first, then {@link isKnownTimeZone} — the same two-step
 * `awsRegionSchema` uses, and for the same reason: the IANA database gains
 * zones, so an allow-list baked in here would refuse a real one. The shape check
 * exists so that a garbage value gets a message about the format rather than
 * "unknown timezone", and so the schema still refuses obvious nonsense on a
 * runtime whose `Intl` cannot help.
 *
 * A campaign stores the zone **as well as** the UTC instant on purpose. The
 * instant is what the scheduler compares against; the zone is what the screen
 * renders and what {@link isQuietHour} reads. Storing only the instant loses the
 * answer to "did they mean 9am in Mumbai?", which is the question a rescheduling
 * customer is actually asking.
 */
export const timeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(
    /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*$/,
    'Timezone must be an IANA name, e.g. Asia/Kolkata or UTC',
  )
  .refine(isKnownTimeZone, 'Unknown timezone — use an IANA name such as Asia/Kolkata');

/** The default a wizard offers when the browser cannot say. India is the launch market. */
export const DEFAULT_TIME_ZONE = 'Asia/Kolkata';

/**
 * The hour (0–23) an instant falls on in a given zone.
 *
 * Returns `null` when the platform cannot tell, and every caller treats that as
 * "no warning" rather than guessing — a quiet-hours warning derived from the
 * wrong clock is worse than none, because it teaches people to ignore the
 * warnings that are right.
 */
export function localHourInTimeZone(instant: Date, timezone: string): number | null {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return null;
  try {
    const text = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: 'numeric',
      hour12: false,
    }).format(instant);
    const hour = Number.parseInt(text, 10);
    if (Number.isNaN(hour)) return null;
    // Older V8 renders midnight as "24" under hour12:false (the h24 cycle).
    // Normalising here rather than reaching for `hourCycle` keeps this working
    // on every runtime that has ever shipped, including the ones whose type
    // definitions predate the option.
    return hour === 24 ? 0 : hour;
  } catch {
    return null;
  }
}

/** True when an instant lands in the tenant's quiet hours. Warning only. */
export function isQuietHour(instant: Date, timezone: string): boolean {
  const hour = localHourInTimeZone(instant, timezone);
  if (hour === null) return false;
  return hour >= QUIET_HOURS_START_HOUR || hour < QUIET_HOURS_END_HOUR;
}

/* -------------------------------------------------------------------------- */
/* The dedupe key — the recipient row's durable address                        */
/* -------------------------------------------------------------------------- */

/**
 * The fields the dedupe key is derived from.
 *
 * A `Pick` of the contact contract rather than a fresh `{ email, phone }`
 * object: there is one contact shape, `./contact.js` owns it, and a rename there
 * has to break this line rather than quietly leave the send path reading a field
 * that no longer exists.
 */
export type DedupeKeySource = Pick<Contact, 'email' | 'phoneE164'>;

/**
 * `campaign_recipients.dedupe_key` — the address a recipient row is keyed on.
 *
 * Until slice 58 this was also THE definition of a person: slice 2's
 * `uniqueRecipients` was `count(DISTINCT coalesce(c.email, c.phone_e164))`, and
 * this function had to agree with it. Since slice 58 a person is
 * `contacts.person_id`, chosen once by `0041_people.sql`, and both the count and
 * the send use that. This key stays what the unique index is built on and what
 * an unsubscribe resolves through, and 0041 proves every key is an address owned
 * by its own row's person — so the key still names exactly one human, and an
 * analytics read can recover them through `person_addresses`.
 *
 * **Normalisation.** Postgres compares the stored `text` byte-for-byte, so
 * `coalesce(email, phone_e164)` is only the same key as this function because
 * the stored values are already normalised: import lower-cases and trims the
 * address, the `contacts_email_lowercase` CHECK holds it there, and phones are
 * stored as E.164. The normalisation below is therefore a no-op on a stored
 * contact and a repair on one in flight — which is what guarantees a key
 * computed in TypeScript equals the key the unique index was built from. It is
 * deliberately *minimal*: no plus-addressing rules, no dot-stripping, no
 * provider-specific folding. Those are opinions about who two addresses belong
 * to, and being wrong means silently not mailing someone.
 *
 * Returns `null` when a contact carries neither — impossible for a stored
 * contact (`contacts_reachable` CHECK), possible for a row in flight. A null key
 * must never be written: a NULL is distinct from every other NULL in a unique
 * index, so a nullable key is a de-duplication guarantee that quietly does
 * nothing.
 *
 * **A limit this key had, and what closed it.** `coalesce` is the right key for
 * an email campaign and only *accidentally* right for WhatsApp. On email, every
 * recipient that is not skipped has an address, so the key is their email and
 * the distinct-key count equals `emailReachable` exactly. On WhatsApp, a contact
 * carrying both an email and a phone keys on its *email*, so it does not collide
 * with a phone-only contact carrying the same number — two rows, one human, two
 * messages, until the two fixes below.
 *
 * **Slice 49 closed the half of that which could send twice.** Until then the
 * only protection was an accident of the data — the two lists are disjoint,
 * nobody has both — and a note here said slice 7 would key on
 * {@link channelAddressForContact} instead. Slice 7 did not, and slice 49 is
 * what made a WhatsApp campaign creatable at all, so `classifyContact` now
 * de-duplicates on the resolved channel address beside this key: one handset,
 * one message, whatever the two contacts carrying it were called.
 *
 * **Slice 58 closed the rest.** The phone-only row and the email row of one human
 * are now one PERSON (`contacts.person_id`), and materialisation sends to each
 * person once, whatever keys their rows carry.
 *
 * The key itself stays `coalesce`, because it is the durable address a re-import
 * cannot change, the unique index is built on it, and an unsubscribe resolves
 * through it. It is no longer what decides who a person is.
 */
export function dedupeKeyForContact(contact: DedupeKeySource): string | null {
  const email = contact.email?.trim().toLowerCase();
  if (email) return email;
  const phone = contact.phoneE164?.trim();
  if (phone) return phone;
  return null;
}

/**
 * The address a message would actually be delivered to on a given channel.
 *
 * Distinct from the dedupe key on purpose. The dedupe key answers "which address
 * is this row keyed on" — since slice 58 "is this the same human" is
 * `contacts.person_id` — and this answers "where does the message go". They coincide for every
 * email recipient that is not skipped, and the send path needs both: a contact
 * with a phone but no email has a perfectly good dedupe key and no destination,
 * which is precisely the {@link CAMPAIGN_SKIP_REASONS.no_email_address} case.
 */
export function channelAddressForContact(contact: DedupeKeySource, channel: Channel): string | null {
  if (channel === 'email') return contact.email?.trim().toLowerCase() || null;
  return contact.phoneE164?.trim() || null;
}

/* -------------------------------------------------------------------------- */
/* Skip reasons — a recipient who got nothing, and why                         */
/* -------------------------------------------------------------------------- */

/**
 * Why a recipient was skipped.
 *
 * Every one of these appears **on screen, next to a person who received
 * nothing**. "Skipped" on its own is not an explanation; it is the shape of one.
 * So each member carries a sentence in {@link CAMPAIGN_SKIP_REASONS}, and the
 * sentences say what happened and whether anything can be done about it.
 *
 * The set is closed and small. A skip reason is written into
 * `campaign_recipients` at materialisation and read back months later, so
 * inventing one at a call site would produce a row nothing can explain. If a new
 * cause of skipping appears, it gets a member and a sentence here first.
 *
 * `no_phone_number` is deliberately absent: slice 5 is email, and a member that
 * nothing can produce is a member nobody keeps accurate. Slice 7 adds it.
 *
 * It is not in `enums.ts` because it is not one of CONTRACT.md's fixed enums —
 * but it is written to a column, so if `@wend/db` mirrors it as a pg enum it
 * must import {@link CAMPAIGN_SKIP_REASON_VALUES} rather than re-typing the
 * members, exactly as the fixed enums are mirrored.
 */
export const CAMPAIGN_SKIP_REASON_VALUES = [
  'not_on_send_allowlist',
  'suppressed',
  'no_email_address',
  'no_phone_number',
  'duplicate_in_segment',
  'campaign_cancelled',
  'rested',
  'holdout',
] as const;
export const campaignSkipReasonSchema = z.enum(CAMPAIGN_SKIP_REASON_VALUES);
export type CampaignSkipReason = z.infer<typeof campaignSkipReasonSchema>;

/** A short badge label and the sentence shown beside the recipient. */
export interface CampaignSkipReasonCopy {
  /** Two or three words, for a badge in a table cell. */
  readonly label: string;
  /** A whole sentence a customer can act on. Never jargon, never "skipped". */
  readonly sentence: string;
}

/**
 * What each skip reason means, in words.
 *
 * These live in the contract rather than in the web app because the API writes
 * the reason and the web renders it, and a sentence maintained on only one side
 * drifts into describing a rule the other side stopped following. The same
 * argument as `sesReadiness`: one derivation, two consumers.
 */
export const CAMPAIGN_SKIP_REASONS: Readonly<Record<CampaignSkipReason, CampaignSkipReasonCopy>> = {
  not_on_send_allowlist: {
    label: 'Not allow-listed',
    sentence:
      'Nothing was sent to this address. Sending is restricted to an allow-list of addresses in this environment, and this one is not on it — a safety guard, not a problem with the contact.',
  },
  suppressed: {
    label: 'Unsubscribed',
    sentence:
      'This person is on your suppression list — they unsubscribed, reported a message as spam, or a previous email hard-bounced. Wend will not email them again unless the suppression is removed.',
  },
  no_email_address: {
    label: 'No email address',
    sentence:
      'This contact has no email address, so an email campaign cannot reach them. They count towards the number of contacts in the segment, and not towards its email reach.',
  },
  no_phone_number: {
    label: 'No phone number',
    sentence:
      'This contact has no phone number, so a WhatsApp campaign cannot reach them. They count towards the number of contacts in the segment, and not towards its WhatsApp reach.',
  },
  duplicate_in_segment: {
    label: 'Duplicate',
    sentence:
      'The same person or phone number as another contact in this segment. No person or number gets this campaign twice.',
  },
  campaign_cancelled: {
    label: 'Cancelled',
    sentence: 'The campaign was cancelled before this message was sent.',
  },
  /*
   * Slice 59. Written only when the campaign was scheduled with "rest people at
   * Critical" switched on, and only for a person at Critical when it was. The
   * threshold is PRESSURE_CRITICAL_AT, interpolated rather than typed, so the
   * sentence cannot disagree with the rule in force today (a row written under
   * an older threshold would show the new number).
   */
  rested: {
    label: 'Rested',
    sentence: `This person had ${String(PRESSURE_CRITICAL_AT)} or more messages in the last ${String(PRESSURE_WINDOW_DAYS)} days when the campaign was scheduled, and it was set to rest people at Critical.`,
  },
  /*
   * Slice 61. Written only when the campaign was scheduled to hold a share
   * back, for the people who fell in that share. Chosen by a hash of campaign
   * and person, so "at random" is true in the sense a reader means: nobody,
   * including whoever scheduled it, picked them.
   */
  holdout: {
    label: 'Held back',
    sentence:
      'Held back at random so Sense can compare the people who got this campaign with the people who did not.',
  },
};

/** The sentence for a reason. Total by construction — every member has one. */
export function campaignSkipReasonSentence(reason: CampaignSkipReason): string {
  return CAMPAIGN_SKIP_REASONS[reason].sentence;
}

/* -------------------------------------------------------------------------- */
/* The send guard                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Where a guard decision was taken.
 *
 * SLICES requires the guard at both points, and this field is how a decision
 * says which one it came from. `pre_send` is the one that holds when a campaign
 * is scheduled today and sends tomorrow: a materialised recipient list outlives
 * the config that produced it, so `materialisation` alone would let yesterday's
 * allow-list authorise today's send.
 */
export const SEND_GUARD_STAGE_VALUES = ['materialisation', 'pre_send'] as const;
export const sendGuardStageSchema = z.enum(SEND_GUARD_STAGE_VALUES);
export type SendGuardStage = z.infer<typeof sendGuardStageSchema>;

/**
 * Why the guard refused, in terms of what an operator would have to change.
 *
 *   - `allowlist_empty` — nothing is configured, so nothing may be delivered.
 *     Its own member rather than a special case of the next one, because the
 *     repair is different and because "everything was skipped" needs to read as
 *     a configured outcome rather than as a bug;
 *   - `not_on_allowlist` — the allow-list has entries and this address is not
 *     one of them;
 *   - `no_address` — there was nothing to compare. It is refused rather than
 *     passed through, because "we could not tell" must never mean "send it".
 */
export const SEND_GUARD_BLOCK_REASON_VALUES = [
  'allowlist_empty',
  'not_on_allowlist',
  'no_address',
] as const;
export const sendGuardBlockReasonSchema = z.enum(SEND_GUARD_BLOCK_REASON_VALUES);
export type SendGuardBlockReason = z.infer<typeof sendGuardBlockReasonSchema>;

/**
 * The guard's answer.
 *
 * A discriminated union rather than a boolean and a nullable reason: "blocked
 * with no reason" and "allowed, but here is why it was blocked" are not states
 * worth being able to build.
 *
 * **It deliberately carries no address.** The decision says *whether*, not *to
 * whom* — the caller already holds the address it asked about. An address that
 * travels on a decision object is an address that reaches a log line by
 * accident, and slice 0's masking convention (`p***@acmeretail.in`) is exactly
 * the thing that gets bypassed when a whole result object is logged. The API's
 * `maskEmail` remains the only way a recipient reaches a log.
 */
export const sendGuardAllowedSchema = z
  .object({ allowed: z.literal(true), stage: sendGuardStageSchema })
  .strict();

/** The refusal half, named because several shapes carry only this branch. */
export const sendGuardBlockedSchema = z
  .object({
    allowed: z.literal(false),
    stage: sendGuardStageSchema,
    reason: sendGuardBlockReasonSchema,
    /** Safe to show a customer, and safe to log. Contains no address. */
    message: z.string(),
  })
  .strict();
export type SendGuardBlocked = z.infer<typeof sendGuardBlockedSchema>;

export const sendGuardDecisionSchema = z.discriminatedUnion('allowed', [
  sendGuardAllowedSchema,
  sendGuardBlockedSchema,
]);
export type SendGuardDecision = z.infer<typeof sendGuardDecisionSchema>;

export const SEND_GUARD_BLOCK_MESSAGES: Readonly<Record<SendGuardBlockReason, string>> = {
  allowlist_empty:
    'The send allow-list is empty, so no message can be delivered from this environment. This is the guard working, not a failure: every recipient is skipped until an address is added to SEND_ALLOWLIST.',
  not_on_allowlist:
    'This address is not on the send allow-list, so nothing was sent to it. Delivery in this environment is restricted to allow-listed addresses.',
  no_address:
    'There was no address to check, so nothing was sent. The guard refuses anything it cannot positively match.',
};

/**
 * Normalises one entry of `SEND_ALLOWLIST`, or rejects it.
 *
 * **Wildcards are not supported and must never be.** `*@bank.in`, `*` or a bare
 * domain would let one careless entry authorise the entire imported list — the
 * precise incident this guard exists to make impossible. An entry that is not a
 * plain, complete address is dropped, and the caller is expected to report the
 * dropped entries loudly at boot rather than start with a guard the operator
 * believes is wider than it is.
 *
 * **One list, both channels.** Slice 7 sends to phone numbers, and it does so
 * through this same list rather than a second one: a `SEND_WHATSAPP_ALLOWLIST`
 * would be a second empty-means-what question to get right, and the answer that
 * matters — nothing goes anywhere it was not typed out in full — has to be the
 * same for a number that rings a personal handset as for an inbox. So an entry
 * is a complete email address **or** a complete E.164 number, and nothing else:
 * `+91` alone is a bare prefix in exactly the way `@bank.in` is a bare domain,
 * and `phoneSchema` refuses it.
 */
export function normaliseAllowlistEntry(entry: string): string | null {
  const value = entry.trim().toLowerCase();
  if (value === '') return null;
  if (value.includes('*') || value.includes('?')) return null;
  // Deliberately the same two rules the rest of the platform uses, so an
  // address that cannot be a contact cannot be an allow-list entry either.
  // Lower-casing is a no-op on a number and is applied to both, because
  // `evaluateSendGuard` lower-cases what it is asked about and the two sides of
  // the comparison must be normalised identically.
  if (emailSchema.safeParse(value).success) return value;
  return phoneSchema.safeParse(value).success ? value : null;
}

/**
 * Parses `SEND_ALLOWLIST` — the config value — into the exact strings the guard
 * will compare against.
 *
 * The parse and the match live in the same file on purpose. A guard whose
 * config is normalised by one function and compared by another is a guard with
 * a gap in it, and the gap shows up either as "it is on the list and it was
 * still blocked" (harmless, confusing) or as a match that should not have
 * happened (not harmless).
 *
 * `undefined`, `''` and a list of nothing but junk all parse to `[]`, and `[]`
 * sends nothing. Missing config is the most likely state of a fresh checkout, so
 * it is the state that must be safest.
 */
export function parseSendAllowlist(raw: string | undefined | null): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  for (const part of raw.split(/[,;\s]+/)) {
    const entry = normaliseAllowlistEntry(part);
    if (entry !== null) seen.add(entry);
  }
  return [...seen].sort();
}

/**
 * **The guard.** One address, one allow-list, one answer.
 *
 * Called at both {@link SendGuardStage}s, so the two enforcement points cannot
 * drift into two different rules. Everything about it fails closed:
 *
 *   - an empty allow-list blocks — SLICES: *an empty allow-list sends nothing at
 *     all*;
 *   - a null, blank or unparseable address blocks;
 *   - the match is **exact on the normalised address**. Not a prefix, not a
 *     domain, not a regex. There is no input to this function that admits an
 *     address the operator did not type out in full.
 *
 * There is no `enabled` parameter, and adding one would be the wrong shape: a
 * caller that can pass `false` is a caller that can be handed `false` by a
 * config parse gone wrong. Turning the guard off is a deliberate, separate act
 * with its own review — which means editing this file.
 */
export function evaluateSendGuard(input: {
  readonly address: string | null | undefined;
  readonly allowlist: readonly string[];
  readonly stage: SendGuardStage;
}): SendGuardDecision {
  const { stage } = input;

  if (input.allowlist.length === 0) {
    return {
      allowed: false,
      stage,
      reason: 'allowlist_empty',
      message: SEND_GUARD_BLOCK_MESSAGES.allowlist_empty,
    };
  }

  const address = input.address?.trim().toLowerCase();
  if (!address) {
    return {
      allowed: false,
      stage,
      reason: 'no_address',
      message: SEND_GUARD_BLOCK_MESSAGES.no_address,
    };
  }

  if (!input.allowlist.includes(address)) {
    return {
      allowed: false,
      stage,
      reason: 'not_on_allowlist',
      message: SEND_GUARD_BLOCK_MESSAGES.not_on_allowlist,
    };
  }

  return { allowed: true, stage };
}

/**
 * The skip reason a guard refusal is recorded as.
 *
 * A table rather than a constant, so the row a person reads says the most
 * accurate thing the guard knew. `no_address` maps to `no_email_address`
 * because that is what actually happened to that contact — the guard should
 * never be the thing that discovers a missing address (materialisation checks
 * for one first), and if it is, the recipient still deserves the true reason.
 */
export const SKIP_REASON_FOR_GUARD_BLOCK: Readonly<
  Record<SendGuardBlockReason, CampaignSkipReason>
> = {
  allowlist_empty: 'not_on_send_allowlist',
  not_on_allowlist: 'not_on_send_allowlist',
  no_address: 'no_email_address',
};

/** The skip reason to write for a blocked recipient. One rule, one row value. */
export function skipReasonForGuardBlock(blocked: SendGuardBlocked): CampaignSkipReason {
  return SKIP_REASON_FOR_GUARD_BLOCK[blocked.reason];
}

/**
 * What the guard would do to this campaign, for the screen that is about to
 * schedule it.
 *
 * `enforced` WAS `z.literal(true)`, on the argument that the contract should
 * have no way to say the guard is off — a `z.boolean()` would be an invitation
 * and the first person in a hurry would accept it, so lifting the guard ought
 * to begin by changing this line and failing every consumer's type check.
 *
 * **That is what happened, and the mechanism did not work the way it intended.**
 * The type check is compile-time; this schema is parsed at RUN time, on a
 * response. So a deployment that lifted the guard in `.env` did not get a review
 * — it got a 500 on `GET /campaigns/:id`, from `readSendGuardStatus` refusing to
 * parse a status it had just correctly built. Every campaign screen broke, and
 * the failure named a zod literal rather than the send guard. The friction
 * landed on the wrong person at the wrong moment, in the wrong words.
 *
 * So it is `z.boolean()` now, and the guarantee it was protecting is kept where
 * it actually holds: `SEND_GUARD_ENFORCED=false` is refused by the config schema
 * unless `SEND_GUARD_LIFT_ACKNOWLEDGEMENT` carries a sentence somebody had to
 * type out, and `SendModule` logs at `error` on every boot for as long as it is
 * lifted. That is the review, and unlike this line it works at the moment the
 * decision is made.
 *
 * **Every consumer must now read `enforced` before reading `deliverable`.** With
 * the guard off, `deliverable` is everybody reachable — the allow-list is not
 * applied — and a screen that says "N are not on the send allow-list" without
 * checking `enforced` is describing a list that is not being consulted.
 *
 * The addresses themselves are **not** on this shape. A count answers the
 * question the screen is asking ("will this actually deliver?") and shipping the
 * operator's test addresses to a browser answers a question nobody asked.
 */
export const sendGuardStatusSchema = z
  .object({
    /**
     * Whether `SEND_ALLOWLIST` is being applied at all. **Read this first.**
     * False means every reachable address is deliverable and the allow-list is
     * inert; see the header for why this is no longer a literal.
     */
    enforced: z.boolean(),
    /** How many addresses are allow-listed. Zero means nothing will deliver. */
    allowlistSize: z.number().int().min(0),
    /**
     * How many of this campaign's reachable addresses are on the allow-list —
     * i.e. **how many messages will actually be delivered**.
     *
     * The honest number, and the one a wizard must show beside the reach: in a
     * development environment "526 people" and "1 delivery" are both true, and
     * showing only the first is how someone concludes the send is broken.
     *
     * `null` when it has not been computed — a list screen does not run the
     * intersection for every row.
     */
    deliverable: z.number().int().min(0).nullable(),
  })
  .strict();
export type SendGuardStatus = z.infer<typeof sendGuardStatusSchema>;

/* -------------------------------------------------------------------------- */
/* Channel reach — the prominent number                                        */
/* -------------------------------------------------------------------------- */

/**
 * What a campaign on one channel actually reaches.
 *
 * The field order is the display order, and it is the whole point of the shape:
 * `reachable` is the headline, and `segmentContacts` / `segmentUniqueRecipients`
 * are context underneath it. Slice 2 is explicit that showing the segment total
 * where the reach belongs is *the* failure mode — it lets someone schedule an
 * email campaign believing they are reaching 1,278 people when 526 of them have
 * an email address.
 *
 * `reachable` is a **ceiling, not a promise**. The suppression list, duplicate
 * addresses across lists and the send guard all cut it further, and only the
 * guard's cut is knowable before materialisation — see
 * {@link sendGuardStatusSchema}. A screen that presents `reachable` as
 * "messages that will be delivered" is making the same class of over-promise one
 * level down.
 */
export const campaignReachSchema = z
  .object({
    channel: channelSchema,
    /** **The headline.** Distinct addresses this channel's send would use — see `segmentCountsSchema`. */
    reachable: z.number().int().min(0),
    /** People in the segment this channel cannot touch. Reported, not hidden. */
    unreachable: z.number().int().min(0),
    /** Context: rows in the segment's lists. Never the headline. */
    segmentContacts: z.number().int().min(0),
    /** Context: de-duplicated people, across both channels. Never the headline. */
    segmentUniqueRecipients: z.number().int().min(0),
  })
  .strict();
export type CampaignReach = z.infer<typeof campaignReachSchema>;

/**
 * Derives the reach of a channel from a segment's counts.
 *
 * **The only supported way to get that number**, for the same reason
 * `sesReadiness` is the only way to get a readiness verdict: the API decides
 * whether a campaign may be scheduled and the wizard decides which number to
 * print in large text, and two implementations of "how many does this reach"
 * drift until one of them prints `contacts`.
 *
 * The mapping is `email → emailReachable`, `whatsapp → phoneReachable`. Both are
 * counts of **distinct addresses** a send would use (see `segment-counts.ts`),
 * which is what makes them comparable with `uniqueRecipients` and never with
 * `contacts`. On WhatsApp `unreachable` also counts people who share a number
 * with somebody earlier in the segment: they have a phone, but not one of their
 * own that this campaign would use.
 *
 * Wend never falls back to another channel to inflate a number. A campaign
 * reaches the people it can reach on the channel it was built for; messaging
 * someone on a channel they were never selected for is not a bigger audience, it
 * is a different product.
 */
export function campaignReach(counts: SegmentCounts, channel: Channel): CampaignReach {
  const reachable = channel === 'email' ? counts.emailReachable : counts.phoneReachable;
  return {
    channel,
    reachable,
    // Against uniqueRecipients, not contacts: both are counts of distinct
    // things, so the subtraction is people rather than rows. `max(0, …)` because
    // the two counts are read by separate aggregates and a segment being
    // re-imported between them must not produce a negative headline.
    unreachable: Math.max(0, counts.uniqueRecipients - reachable),
    segmentContacts: counts.contacts,
    segmentUniqueRecipients: counts.uniqueRecipients,
  };
}

/* -------------------------------------------------------------------------- */
/* Progress — computed on read, never stored                                   */
/* -------------------------------------------------------------------------- */

/**
 * Recipients by status.
 *
 * An explicit object rather than `z.record(recipientStatusSchema, …)`, so that a
 * missing status is a parse failure instead of an `undefined` that renders as a
 * blank where a zero belongs. The type is total; every consumer can index it
 * without a fallback.
 */
export const recipientStatusCountsSchema = z
  .object({
    pending: z.number().int().min(0),
    sent: z.number().int().min(0),
    /** Slice 6. No delivery webhook exists yet, so slice 5 leaves this at zero. */
    delivered: z.number().int().min(0),
    /** Slice 6, as above. */
    bounced: z.number().int().min(0),
    failed: z.number().int().min(0),
    skipped: z.number().int().min(0),
  })
  .strict();
export type RecipientStatusCounts = z.infer<typeof recipientStatusCountsSchema>;

export const EMPTY_RECIPIENT_STATUS_COUNTS: RecipientStatusCounts = {
  pending: 0,
  sent: 0,
  delivered: 0,
  bounced: 0,
  failed: 0,
  skipped: 0,
};

/**
 * Skipped recipients by reason. Same shape rule as the status counts, same
 * reason: a reason with nobody in it is a zero, not an absence.
 */
export const campaignSkipReasonCountsSchema = z
  .object({
    not_on_send_allowlist: z.number().int().min(0),
    suppressed: z.number().int().min(0),
    no_email_address: z.number().int().min(0),
    no_phone_number: z.number().int().min(0),
    duplicate_in_segment: z.number().int().min(0),
    campaign_cancelled: z.number().int().min(0),
    rested: z.number().int().min(0),
    holdout: z.number().int().min(0),
  })
  .strict();
export type CampaignSkipReasonCounts = z.infer<typeof campaignSkipReasonCountsSchema>;

export const EMPTY_SKIP_REASON_COUNTS: CampaignSkipReasonCounts = {
  not_on_send_allowlist: 0,
  suppressed: 0,
  no_email_address: 0,
  no_phone_number: 0,
  duplicate_in_segment: 0,
  campaign_cancelled: 0,
  rested: 0,
  holdout: 0,
};

/**
 * Turns a `GROUP BY status` result into total counts.
 *
 * Exists because of a specific, repeatable bug: a grouped aggregate returns **no
 * row** for a status nothing has, so building the object from the rows alone
 * leaves `skipped` undefined on a campaign that skipped nobody — and the screen
 * renders a blank where "0" belongs. Starting from zeros is the fix, and it is
 * the same shape as `totalsFor` in `segment-counts.ts`.
 */
export function recipientStatusCounts(
  rows: readonly { readonly status: string; readonly count: number }[],
): RecipientStatusCounts {
  const counts: RecipientStatusCounts = { ...EMPTY_RECIPIENT_STATUS_COUNTS };
  for (const row of rows) {
    const status = recipientStatusSchema.safeParse(row.status);
    if (status.success) counts[status.data] = row.count;
  }
  return counts;
}

/**
 * As above, for `GROUP BY skip_reason`.
 *
 * `campaign_recipients.skip_reason` is a `text` column, so a value outside
 * {@link CAMPAIGN_SKIP_REASON_VALUES} is physically possible and is **dropped
 * here** — a reason nothing can explain must not be given a number on a screen.
 * The consequence is that the breakdown would then not add up to
 * `counts.skipped`, so the write path must only ever store a member of that
 * tuple. It is the one rule this helper cannot enforce for itself.
 */
export function campaignSkipReasonCounts(
  rows: readonly { readonly reason: string | null; readonly count: number }[],
): CampaignSkipReasonCounts {
  const counts: CampaignSkipReasonCounts = { ...EMPTY_SKIP_REASON_COUNTS };
  for (const row of rows) {
    if (row.reason === null) continue;
    const reason = campaignSkipReasonSchema.safeParse(row.reason);
    if (reason.success) counts[reason.data] = row.count;
  }
  return counts;
}

/**
 * What the campaign detail screen polls.
 *
 * **Computed on read, never stored** — the segment-counts precedent, and here
 * the argument is stronger than it was there. A stored `sent_count` is a second
 * source of truth for something `campaign_recipients` already knows, updated by
 * the same worker that writes the rows; it therefore drifts on exactly the
 * events that matter most — a redelivered job, a crash between the row update
 * and the counter update, a manual repair. `messages.idempotency_key` makes the
 * *send* exactly-once, and nothing gives an incremented counter the same
 * property. The counts are two grouped aggregates over an indexed
 * `campaign_id`:
 *
 * ```sql
 * SELECT status, count(*) FROM campaign_recipients
 *  WHERE campaign_id = $1 GROUP BY status;
 *
 * SELECT skip_reason, count(*) FROM campaign_recipients
 *  WHERE campaign_id = $1 AND status = 'skipped' GROUP BY skip_reason;
 * ```
 *
 * There is deliberately **no `total` field**: it is `sum(counts)`, and a stored
 * total is a number that can contradict the numbers printed next to it. Use
 * {@link campaignProgressReading}.
 *
 * `materialisedAt` is what separates "no recipients yet" from "no recipients" —
 * a scheduled campaign whose list has not been built shows all zeros, and
 * without this field a screen cannot tell that from a segment that reached
 * nobody. They deserve very different words.
 */
export const campaignProgressSchema = z
  .object({
    status: campaignStatusSchema,
    counts: recipientStatusCountsSchema,
    skippedByReason: campaignSkipReasonCountsSchema,
    /** When the recipient list was built. Null means it has not been. */
    materialisedAt: isoDateTimeSchema.nullable(),
    /** When the first message was handed to the provider. */
    startedAt: isoDateTimeSchema.nullable(),
    /** When the campaign reached a terminal status. */
    finishedAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type CampaignProgress = z.infer<typeof campaignProgressSchema>;

/**
 * How far along a campaign is, in numbers a bar can be drawn from.
 *
 * The trap this exists to close: a bar drawn as `sent / total` **never reaches
 * 100%** when anything was skipped, and in a guarded environment nearly
 * everything is skipped — so a perfectly successful send sits at 0.2% and looks
 * hung. A skipped recipient is *resolved*: nothing more will happen to them, and
 * honest progress counts them as done while reporting them separately.
 */
export interface CampaignProgressReading {
  /** Every materialised recipient. `sum(counts)`, derived here and only here. */
  readonly total: number;
  /** Recipients a message was or will be attempted for: `total - skipped`. */
  readonly sendable: number;
  /** Nothing further will happen to these: everything except `pending`. */
  readonly resolved: number;
  /** Still waiting. The number that has to reach zero. */
  readonly remaining: number;
  /** 0–100, over `total` — skipped included, because they are resolved. */
  readonly percentComplete: number;
  /** True once the list exists and nothing is pending. */
  readonly complete: boolean;
  /** The list has not been built yet, so zeros mean "not yet", not "nobody". */
  readonly notMaterialised: boolean;
}

export function campaignProgressReading(progress: CampaignProgress): CampaignProgressReading {
  const c = progress.counts;
  const total = c.pending + c.sent + c.delivered + c.bounced + c.failed + c.skipped;
  const resolved = total - c.pending;
  const notMaterialised = progress.materialisedAt === null;

  return {
    total,
    sendable: total - c.skipped,
    resolved,
    remaining: c.pending,
    // A campaign that materialised to nobody is finished, not 0% done. Reporting
    // 0 there would leave a bar sitting empty on a campaign that will never move.
    percentComplete: total === 0 ? (notMaterialised ? 0 : 100) : Math.round((resolved / total) * 100),
    complete: !notMaterialised && c.pending === 0,
    notMaterialised,
  };
}

/* -------------------------------------------------------------------------- */
/* Status transitions                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Only a draft can be edited.
 *
 * Once a campaign is scheduled its recipient list may already exist, and editing
 * the template or the segment underneath a materialised list produces messages
 * nobody chose to send. Cancel, edit, schedule again — three deliberate steps
 * rather than one silent one.
 */
export function isCampaignEditable(status: CampaignStatus): boolean {
  return status === 'draft';
}

/**
 * Terminal statuses. Nothing moves out of these.
 *
 * Slice 18's two additions are deliberately **not** here.
 * `holding_between_waves` releases itself once the hold elapses and the delivery
 * reports clear the gate, and `halted_delivery_risk` waits for a person — both
 * are pauses, on `paused_insufficient_credits`'s precedent, and a screen that
 * treated either as finished would tell somebody their campaign was over while
 * several hundred recipients were still `pending`.
 */
export function isCampaignTerminal(status: CampaignStatus): boolean {
  return status === 'sent' || status === 'failed' || status === 'cancelled';
}

/**
 * Cancel is available up to and including mid-send.
 *
 * Cancelling a `sending` campaign cannot un-send what the provider has already
 * accepted — it stops the rest. The recipients that never got their turn become
 * `skipped` with {@link CAMPAIGN_SKIP_REASONS.campaign_cancelled} rather than
 * staying `pending` for ever, so the progress numbers settle and the screen can
 * say what happened to each person.
 *
 * **Slice 18's two states are cancellable, and `halted_delivery_risk` is the
 * important one.** A campaign that halted itself because a cold list is bouncing
 * is the campaign somebody is most likely to want to abandon rather than resume,
 * and a halt that could only be resumed — never stopped — would leave several
 * hundred recipients `pending` for ever with no way to settle them. The halt
 * exists to make continuing a deliberate act; it must not make *stopping* an
 * impossible one.
 */
export function isCampaignCancellable(status: CampaignStatus): boolean {
  return (
    status === 'scheduled' ||
    status === 'sending' ||
    status === 'paused_insufficient_credits' ||
    status === 'holding_between_waves' ||
    status === 'halted_delivery_risk'
  );
}

/* -------------------------------------------------------------------------- */
/* Read                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The template a campaign sends, as the campaign screen needs it.
 *
 * Denormalised onto the campaign read so the detail screen can say "Summit
 * invite (approved)" without a second request — and, more importantly, so it can
 * warn when the template a scheduled campaign points at is no longer `approved`.
 */
export const campaignTemplateRefSchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    type: templateTypeSchema,
    status: templateStatusSchema,
  })
  .strict();
export type CampaignTemplateRef = z.infer<typeof campaignTemplateRefSchema>;

export const campaignSegmentRefSchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
  })
  .strict();
export type CampaignSegmentRef = z.infer<typeof campaignSegmentRefSchema>;

/**
 * A campaign, in full.
 *
 * `reach` and `progress` are both computed on read and both carried here so the
 * first paint of the detail screen is one request. The screen then polls
 * `GET /campaigns/:id/progress` — {@link campaignProgressResponseSchema}, the
 * same shape — rather than re-reading the whole campaign every two seconds.
 *
 * `fromEmail` is on the campaign rather than on the template because it is a
 * property of *who is sending*, not of *what is being sent*: the same template
 * goes out from a different identity when a tenant verifies a new domain, and
 * the address has to be one of the SES identities the tenant's own account has
 * verified. That check needs AWS and therefore cannot be a schema rule — see
 * {@link campaignScheduleIssues}. It is **nullable** since slice 49: a WhatsApp
 * campaign is sent from the number on the tenant's Meta account and has no from
 * address at all, and `''` in its place would be a screen printing "sent from"
 * followed by nothing.
 */
export const campaignSchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    /** Fixed at creation and never updatable — see {@link updateCampaignRequestSchema}. */
    channel: channelSchema,
    status: campaignStatusSchema,
    template: campaignTemplateRefSchema,
    segment: campaignSegmentRefSchema,
    /** Verified SES identity the message is sent from. Null on WhatsApp. */
    fromEmail: z.string().nullable(),
    /** Display name beside the from address, e.g. `Acme Retail`. */
    fromName: z.string().nullable(),
    /**
     * The WhatsApp half: what fills this campaign's positions, and what its
     * template currently needs filled. **Null means this is not a WhatsApp
     * campaign**, which is a different statement from an empty binding list.
     *
     * A nullable SUB-OBJECT rather than three nullable arrays hung off the
     * campaign, because the nesting is what keeps three facts apart that a
     * flatter shape would collapse into "no positions":
     *
     *   - `whatsapp === null` — an email campaign. There are no positions here
     *     and the question does not apply;
     *   - `whatsapp.template === null` — a WhatsApp campaign whose template
     *     could not be read, so **nothing is known** about what it needs. The
     *     panel must say so rather than draw an empty, reassuring list;
     *   - `whatsapp.template.requiredPositions === []` — read fine, and it needs
     *     nothing. Most utility templates are this.
     *
     * `requiredPositions` is **computed on every read and never stored**: the
     * bindings belong to the campaign and the template keeps moving underneath
     * it, so a stored list is a fact that was true when somebody last pressed
     * Save.
     */
    whatsapp: z
      .object({
        bindings: z.array(waVariableBindingSchema),
        template: z
          .object({
            /**
             * Positions the template's header, body and URL buttons need — the
             * only one of the three lists this read carries.
             *
             * `unboundPositions` and `unusedPositions` were here until slice
             * 49's review: the browser derived both for itself anyway, so the
             * wire was carrying a second answer to a question `waBindingState`
             * already answers once. Both sides call it now.
             */
            requiredPositions: z.array(waPositionSchema),
          })
          .strict()
          .nullable(),
      })
      .strict()
      .nullable(),
    /** The instant the scheduler compares against. Null while still a draft. */
    scheduledAt: isoDateTimeSchema.nullable(),
    /** The zone `scheduledAt` was chosen in. See {@link timeZoneSchema}. */
    timezone: timeZoneSchema,
    /** Channel reach — the prominent number. Computed, never stored. */
    reach: campaignReachSchema,
    /** Counts by recipient status. Computed, never stored. */
    progress: campaignProgressSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();
export type Campaign = z.infer<typeof campaignSchema>;

export const campaignResponseSchema = z.object({ campaign: campaignSchema });
export type CampaignResponse = z.infer<typeof campaignResponseSchema>;

/**
 * The polled shape. Deliberately the same {@link campaignProgressSchema} the
 * detail read carries, so a screen has one renderer for both and the poll can
 * never report a different vocabulary than the page it is updating.
 */
export const campaignProgressResponseSchema = z.object({ progress: campaignProgressSchema });
export type CampaignProgressResponse = z.infer<typeof campaignProgressResponseSchema>;

/**
 * Row shape for the campaign list.
 *
 * `reach` is on the row, for the same reason segment counts are on the segment
 * list row: the number that says how many people this touches is exactly the
 * number that must not be one click away. `progress` is *not* — a list of
 * twenty campaigns would run forty aggregates to draw twenty bars, so a row
 * carries the two numbers a list actually shows.
 *
 * Slice 49 adds **nothing** here, and it is the same argument one step further:
 * a row shows no bindings, so carrying them would make a list of twenty
 * campaigns read twenty template documents to draw twenty rows that never
 * mention a position. The WhatsApp block lives on {@link campaignSchema}, which
 * is one campaign and one read.
 */
export const campaignSummarySchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    channel: channelSchema,
    status: campaignStatusSchema,
    templateName: z.string(),
    segmentName: z.string(),
    scheduledAt: isoDateTimeSchema.nullable(),
    timezone: timeZoneSchema,
    reach: campaignReachSchema,
    /** Recipients that have been sent. Zero until the send starts. */
    sentCount: z.number().int().min(0),
    /** Recipients that were skipped. The other half of an honest row. */
    skippedCount: z.number().int().min(0),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();
export type CampaignSummary = z.infer<typeof campaignSummarySchema>;

export const CAMPAIGN_SORT_VALUES = ['createdAt', 'scheduledAt', 'name'] as const;
export const campaignSortSchema = z.enum(CAMPAIGN_SORT_VALUES);
export type CampaignSort = z.infer<typeof campaignSortSchema>;

/**
 * The longest search text the campaigns list accepts. A constant, not a bare
 * `120` in the schema, because the campaigns page refuses a longer search on
 * the box itself (slice 65) — sent, it is a 400 the page could only draw as
 * "could not load your campaigns", which reads as an outage. Until slice 65
 * that page capped the box at the Meta ad drafts' shorter limit, because one
 * box searched both lists; the drafts moved to Reach's page and took that cap
 * with them, so the campaigns list's own limit is the one the box keeps.
 */
export const CAMPAIGN_SEARCH_MAX = 120;

export const campaignListQuerySchema = paginationQuerySchema.extend({
  /** Matches the campaign name, case-insensitive. */
  search: z.string().trim().max(CAMPAIGN_SEARCH_MAX).optional(),
  status: campaignStatusSchema.optional(),
  channel: channelSchema.optional(),
  sort: campaignSortSchema.default('createdAt'),
  direction: sortDirectionSchema.default('desc'),
});
export type CampaignListQuery = z.infer<typeof campaignListQuerySchema>;

export const campaignListResponseSchema = paginated(campaignSummarySchema);
export type CampaignListResponse = z.infer<typeof campaignListResponseSchema>;

export const campaignParamsSchema = z.object({ campaignId: uuidSchema });
export type CampaignParams = z.infer<typeof campaignParamsSchema>;

/* -------------------------------------------------------------------------- */
/* Recipients — where a skip reason is actually read                           */
/* -------------------------------------------------------------------------- */

/**
 * One row of the campaign's recipient list.
 *
 * This is the screen the skip reasons were written for. A recipient who received
 * nothing shows their status, the reason, and the sentence from
 * {@link CAMPAIGN_SKIP_REASONS} — "skipped" with no explanation next to a real
 * customer's contact is how a person concludes the product is broken.
 *
 * `skipReason` is non-null exactly when `status` is `skipped`. That pairing is a
 * CHECK in the database rather than a union here, because the row is read far
 * more often than it is validated and a discriminated union would make every
 * consumer narrow before reading an id.
 *
 * `messageStatus` is null until a `messages` row exists. It is carried through
 * because the two statuses answer different questions — the recipient's status
 * is about this person, the message's is about the provider — and slice 6's
 * `unknown` (accepted by the provider, never recorded) is only ever visible
 * here.
 */
export const campaignRecipientSchema = z
  .object({
    id: uuidSchema,
    contactId: uuidSchema,
    name: z.string().nullable(),
    /** The address this recipient would be, or was, sent to. */
    address: z.string().nullable(),
    status: recipientStatusSchema,
    /** Non-null exactly when `status` is `skipped`. */
    skipReason: campaignSkipReasonSchema.nullable(),
    messageStatus: messageStatusSchema.nullable(),
    /** When the provider accepted the message. Null unless it did. */
    sentAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type CampaignRecipient = z.infer<typeof campaignRecipientSchema>;

export const campaignRecipientListQuerySchema = paginationQuerySchema.extend({
  status: recipientStatusSchema.optional(),
  /** Filters to one reason — "show me everyone the guard blocked". */
  skipReason: campaignSkipReasonSchema.optional(),
  search: z.string().trim().max(120).optional(),
});
export type CampaignRecipientListQuery = z.infer<typeof campaignRecipientListQuerySchema>;

export const campaignRecipientListResponseSchema = paginated(campaignRecipientSchema);
export type CampaignRecipientListResponse = z.infer<typeof campaignRecipientListResponseSchema>;

/* -------------------------------------------------------------------------- */
/* WhatsApp preview — what the send would actually put in {{1}}                */
/* -------------------------------------------------------------------------- */

/**
 * Preview one WhatsApp message, against bindings that have **not been saved**.
 *
 * The body carries the bindings being edited, because a preview that could only
 * run against what is already stored is a preview of the previous decision — the
 * person is looking at the row they just changed and wants to know what it does.
 *
 * **The API must resolve this with the send's own functions and nothing else.**
 * `loadWhatsAppMessageSource` and `waValuesForRecipient` are what the runner
 * calls; a preview that substituted positions with a loop of its own is
 * confidently right on screen and wrong on the wire, which is the same shape as
 * a rate computed over the wrong denominator and is discovered the same way —
 * after a thousand people have read the wrong word.
 */
export const campaignWaPreviewRequestSchema = z
  .object({ bindings: waBindingListSchema })
  .strict();
export type CampaignWaPreviewRequest = z.infer<typeof campaignWaPreviewRequestSchema>;

export const waPreviewValueSchema = z
  .object({
    position: waPositionSchema,
    source: z.string(),
    /** What the send would put here, after fallback. Never empty — Meta refuses one. */
    value: z.string(),
    /** True when the contact's own field was empty and the fallback was used. */
    usedFallback: z.boolean(),
  })
  .strict();
export type WaPreviewValue = z.infer<typeof waPreviewValueSchema>;

/**
 * The message as one real contact from the segment would receive it, plus the
 * one counted figure this preview owes.
 *
 * `cohort` is the honest number, and it is in the contract rather than being a
 * nicety because of the failure mode that produces no error anywhere: misspell
 * `extras.Branch` as `extras.Brnach`, every value resolves empty, every position
 * takes its fallback, every message sends, every count is green, and 1,284
 * people are told "your branch". Nothing else on this response would notice.
 * `fallbackByPosition` is what a person sees before scheduling.
 *
 * **`null` is not zero, twice over.** `cohort === null` means nobody in the
 * segment is reachable on WhatsApp, so there was no cohort to count — not that
 * nobody falls back. `sample === null` means the same about the one contact this
 * resolved against.
 */
export const campaignWaPreviewSchema = z
  .object({
    /**
     * The contact this resolved against. **No address and no phone number
     * travels here** — a preview is a screen, and a screen does not need a real
     * customer's number to show what `{{1}}` says.
     */
    sample: z.object({ contactId: uuidSchema, name: z.string().nullable() }).strict().nullable(),
    values: z.array(waPreviewValueSchema),
    /** Null when the header is media or absent; `headerMedia` says which it was. */
    header: z.string().nullable(),
    headerMedia: waMediaFormatSchema.nullable(),
    body: z.string(),
    footer: z.string().nullable(),
    buttons: z.array(
      z
        .object({
          index: z.number().int().min(0),
          label: z.string(),
          /** Null for a quick reply, which has no URL to resolve. */
          url: z.string().nullable(),
        })
        .strict(),
    ),
    cohort: z
      .object({
        /** People in the segment with a phone number. Never the segment size. */
        reachable: z.number().int().min(0),
        fallbackByPosition: z.array(
          z.object({ position: waPositionSchema, count: z.number().int().min(0) }).strict(),
        ),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type CampaignWaPreview = z.infer<typeof campaignWaPreviewSchema>;

export const campaignWaPreviewResponseSchema = z
  .object({ preview: campaignWaPreviewSchema })
  .strict();
export type CampaignWaPreviewResponse = z.infer<typeof campaignWaPreviewResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Write                                                                       */
/* -------------------------------------------------------------------------- */

export const campaignNameSchema = z.string().trim().min(1).max(MAX_CAMPAIGN_NAME_LENGTH);

/** Display name beside the from address. Cleared to null, like every other one. */
export const campaignFromNameSchema = z
  .string()
  .trim()
  .max(120)
  .refine((value) => !/[\r\n]/.test(value), 'This field is a single line')
  .transform((value) => (value === '' ? null : value))
  .nullable();

/**
 * Create a campaign.
 *
 * A campaign names a template and a segment. Both ids must belong to the calling
 * tenant, which is not something a zod schema can check and is not left to the
 * API alone either: the composite foreign keys carry `tenant_id`, and a foreign
 * key check is not subject to RLS.
 *
 * `channel` decides which of the segment's two reach numbers is the campaign's
 * headline, and it is required rather than inferred from the template so that a
 * wizard can show the reach **at the moment the channel is chosen** — SLICES is
 * explicit that this is where the number has to appear. The API still refuses a
 * channel the template cannot serve; see {@link campaignScheduleIssues}.
 *
 * `scheduledAt` is optional here and required by {@link scheduleCampaignRequestSchema}.
 * Choosing an audience and choosing a time are two decisions and a person makes
 * them in either order; a draft with no time is an ordinary state.
 *
 * **`channel` IS THE FULL ENUM AS OF SLICE 49.** It was `z.literal('email')`
 * carrying a note that slice 7 would widen it; slice 7 shipped the templates,
 * the submission and the whole send path and never touched this line, so for
 * forty-two slices no WhatsApp campaign could be created through the API at all
 * while every screen behind it worked. `NewCampaignPage` probes this schema with
 * a `channel: 'whatsapp'` body and renders its channel picker only if the parse
 * succeeds — so widening the contract is what puts the picker on the screen, and
 * that mechanism is preserved rather than replaced.
 *
 * **ONE `.strict()` OBJECT WITH A `.superRefine()`, NOT A DISCRIMINATED UNION**,
 * and both reasons are binding:
 *
 *   1. that probe parses a body with `channel: 'whatsapp'` and **no**
 *      `fromEmail`, and zod 3's `discriminatedUnion` cannot keep the
 *      discriminator's `.default('email')` — so a union would break the very
 *      mechanism that renders the picker, and break it silently;
 *   2. only a `superRefine` can land a refusal on `fromEmail` and on
 *      `waBindings.2.position` **by name**. A union's failure is "no variant
 *      matched", which is a sentence with no field on it, and the house rule is
 *      that a refusal names the field it is about.
 *
 * So `fromEmail` is optional in the *shape* and conditional in the
 * *refinement*: email requires it, WhatsApp refuses it outright, and the
 * database holds the same rule in both directions —
 * `campaigns_from_email_channel` in 0035, `(channel = 'email') = (from_email IS
 * NOT NULL)`.
 */
export const createCampaignRequestSchema = z
  .object({
    name: campaignNameSchema,
    channel: channelSchema.default('email'),
    templateId: uuidSchema,
    segmentId: uuidSchema,
    /**
     * Email only, and one of the tenant's verified SES identities — which is a
     * service check, not a shape one. Optional here and **required by the
     * refinement below**; see the header for why it is not a union arm.
     */
    fromEmail: emailSchema.optional(),
    fromName: campaignFromNameSchema.optional().default(null),
    /**
     * WhatsApp only. **Absent is not `[]`**: absent means "a WhatsApp campaign
     * nobody has bound yet", and `[]` on an email campaign is a claim about
     * positions that campaign does not have. The refinement refuses the second
     * and the column's CHECK refuses it again.
     */
    waBindings: waBindingListSchema.optional(),
    scheduledAt: isoDateTimeSchema.nullable().optional().default(null),
    timezone: timeZoneSchema.default(DEFAULT_TIME_ZONE),
  })
  .strict()
  .superRefine((body, ctx) => {
    if (body.channel === 'email') {
      // R1. The column is NOT NULL for an email campaign and AWS refuses a
      // message with no sender, so this is refused at the edge where a person
      // can still pick one rather than at a 23514 nobody can read.
      if (body.fromEmail === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fromEmail'],
          message: 'An email campaign needs a from address. Choose one of the identities your SES account has verified.',
        });
      }
      // R4. An EMPTY ARRAY IS REFUSED TOO, deliberately. `waBindings: []` on an
      // email campaign is not an absence — it is a claim about a campaign that
      // has no positions to bind, and accepting it would write `[]` into a
      // column whose CHECK says only a WhatsApp campaign may have one.
      if (body.waBindings !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['waBindings'],
          message: 'An email campaign has no WhatsApp positions to fill.',
        });
      }
      return;
    }

    // R0. A CHANNEL THIS CONTRACT HAS NOT BEEN TAUGHT IS REFUSED HERE.
    //
    // `channelSchema` already holds `sms`, and slice 54 builds it. Until then
    // every rule below this line is WhatsApp's, and `campaignScheduleIssues`
    // has no sending-account branch for a third channel — so an SMS campaign
    // created today would pass a gate that simply had nothing to say about it,
    // and stop at the runner with a sentence about a from address. A refusal
    // naming the channel is the honest version of that, and slice 54 deletes
    // this rule in the same commit that teaches the gate.
    if (body.channel !== 'whatsapp') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['channel'],
        message: `Wend cannot send a ${body.channel} campaign yet. Choose email or WhatsApp.`,
      });
      return;
    }

    // R2 and R3. Not "ignored", refused: a WhatsApp campaign carrying a
    // leftover address is the row that makes a screen print "sent from
    // <address>" about a message Meta sent from a phone number.
    if (body.fromEmail !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fromEmail'],
        message: 'A WhatsApp campaign is sent from the number on your connected Meta account, so it has no from address.',
      });
    }
    if (body.fromName !== undefined && body.fromName !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fromName'],
        message: 'A WhatsApp campaign is sent under the verified name on your Meta account, so it has no from name.',
      });
    }
  });
export type CreateCampaignRequest = z.infer<typeof createCampaignRequestSchema>;

/**
 * Update a draft — any subset, one endpoint.
 *
 * Only a draft. {@link isCampaignEditable} is the rule, and the API enforces it:
 * changing the segment under a materialised recipient list produces messages
 * nobody chose to send, and there is no version of that which is a patch.
 *
 * **`channel` IS NOT HERE AND MUST NOT BE.** It is fixed at creation. Changing
 * it would orphan `from_email` against `wa_bindings` — both of 0035's CHECKs are
 * two-directional, so the write would simply be refused by the database with a
 * 23514 — and, worse where it did not, it would silently change *which of the
 * segment's two reach numbers* the campaign has been claiming: 526 by email and
 * 752 by WhatsApp are not the same people, and a campaign that swapped channels
 * under a person who had already read its reach would be a different audience
 * with the same name. Cancel, create the other one, delete this one.
 *
 * The channel-conditional refusals that belong here cannot be schema rules,
 * because this body carries no channel for zod to read: `CampaignsService.update`
 * makes them against the **stored** row.
 */
export const updateCampaignRequestSchema = z
  .object({
    name: campaignNameSchema.optional(),
    templateId: uuidSchema.optional(),
    segmentId: uuidSchema.optional(),
    fromEmail: emailSchema.optional(),
    fromName: campaignFromNameSchema.optional(),
    /** WhatsApp only, refused on an email campaign by the service. */
    waBindings: waBindingListSchema.optional(),
    scheduledAt: isoDateTimeSchema.nullable().optional(),
    timezone: timeZoneSchema.optional(),
  })
  .strict()
  .refine(
    (body) => Object.keys(body).length > 0,
    // The list is the endpoint's own account of what it takes, so it is kept in
    // step with the shape above: a stale list is a sentence that lies about the
    // API to the one person reading it at the moment they are stuck.
    'Nothing to update: send a name, templateId, segmentId, fromEmail, fromName, waBindings, scheduledAt or timezone',
  );
export type UpdateCampaignRequest = z.infer<typeof updateCampaignRequestSchema>;

/**
 * Schedule a campaign — and what a schema can and cannot refuse.
 *
 * **Refused here, by the shape:** a malformed or unknown timezone, and a
 * `scheduledAt` more than {@link SCHEDULE_PAST_GRACE_MINUTES} in the past. The
 * past-time rule is a refinement rather than a service check because it needs
 * nothing but the request itself, and a rule that needs nothing but the request
 * belongs where it cannot be skipped.
 *
 * **Cannot be refused here — these are service checks**, because each needs a
 * row, an account or a clock this schema cannot see. They are all expressed as
 * {@link campaignScheduleIssues} so the API's refusal and the wizard's disabled
 * button come from one function:
 *
 *   - **the template is `approved`** — needs the `templates` row. SLICES:
 *     a campaign can only be scheduled when its template is approved;
 *   - **the template's type matches the channel** — an email campaign cannot
 *     send a WhatsApp template;
 *   - **the segment has recipients on this channel** — needs the live segment
 *     counts, which are computed on read and can be zero by the time this runs;
 *   - **where this deployment hosts images, every image in the template can be
 *     hosted** — needs the deployment's delivery mode, the template's blocks and
 *     its asset rows. A hosted image is served from a public URL rather than
 *     carried in the message, so one without a public id arrives broken rather
 *     than arriving late. Where images are embedded nothing is read and nothing
 *     can be refused;
 *   - **SES is connected and can send** — needs `sesReadiness` over a stored
 *     check, and `fromEmail` has to be one of that account's verified
 *     identities. Neither is knowable without AWS;
 *   - **the campaign is still a draft** — needs the current row, and it is the
 *     one check that must be re-read inside the scheduling transaction rather
 *     than from whatever the wizard last saw.
 *
 * "Send now" is expressed by passing the current instant. There is deliberately
 * no separate immediate-send path: two send paths mean one of them is
 * under-tested, and it will be the one carrying the unusual message.
 */
/**
 * Slice 61: the shares a campaign may hold back, in percent. Here rather than in
 * `holdout.ts` because this contract is imported by the ones `holdout.ts`
 * depends on — defining it there would make an import cycle.
 */
export const HOLDOUT_PERCENT_VALUES = [5, 10, 20] as const;
export type HoldoutPercent = (typeof HOLDOUT_PERCENT_VALUES)[number];
export const holdoutPercentSchema = z.union([z.literal(5), z.literal(10), z.literal(20)]);

export const scheduleCampaignRequestSchema = z
  .object({
    scheduledAt: isoDateTimeSchema,
    timezone: timeZoneSchema,
    /**
     * Slice 59: leave out everybody at Critical message pressure
     * (`PRESSURE_CRITICAL_AT` or more messages in `PRESSURE_WINDOW_DAYS`) when
     * the recipient list is built. Honoured only for a workspace whose plan
     * includes Sense. Off
     * unless asked for: resting somebody means they do not get this campaign,
     * and that is a choice a person makes, not a default. Absent means off;
     * only an explicit `true` rests anybody.
     */
    restCritical: z.boolean().optional(),
    /**
     * Slice 61: hold this share of the people back, chosen by a hash of campaign
     * and person, to measure the campaign against them. Honoured only for a
     * plan that includes Sense; absent means nobody is held back.
     */
    holdoutPercent: holdoutPercentSchema.optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    const when = Date.parse(body.scheduledAt);
    if (Number.isNaN(when)) return;
    const floor = Date.now() - SCHEDULE_PAST_GRACE_MINUTES * 60_000;
    if (when < floor) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduledAt'],
        message: 'That time has already passed. Pick a time from now onwards.',
      });
    }
  });
export type ScheduleCampaignRequest = z.infer<typeof scheduleCampaignRequestSchema>;

/* -------------------------------------------------------------------------- */
/* Schedule readiness — one derivation, two consumers                          */
/* -------------------------------------------------------------------------- */

/**
 * What can be wrong at the moment somebody presses Schedule.
 *
 * Severity carries the difference between "refused" and "said out loud", exactly
 * as slice 4's template approval issues do — and the quiet-hours member is the
 * reason the distinction exists here too: SLICES settles that quiet hours warn
 * and do not block.
 */
export const CAMPAIGN_SCHEDULE_ISSUE_VALUES = [
  /* Blockers */
  'campaign_not_draft',
  'template_not_approved',
  'template_channel_mismatch',
  'no_reachable_recipients',
  'template_images_not_hostable',
  'template_attachments_not_linkable',
  'integration_not_ready',
  'from_address_not_verified',
  'scheduled_in_past',
  /* Blockers — slice 49, and every one of them is WhatsApp's own. The two SES
     blockers above — and `sending_account_sandboxed`, which is advice rather
     than a blocker and sits in the group below — cannot fire on a WhatsApp
     campaign at all: see the sending account block in `campaignScheduleIssues`. */
  'wa_bindings_incomplete',
  'whatsapp_account_not_connected',
  'whatsapp_number_not_verified',
  'whatsapp_token_expired',
  /* Advice */
  'quiet_hours',
  'send_allowlist_empty',
  'send_guard_restricts_delivery',
  'sending_account_sandboxed',
  /* Advice — slice 49. `wa_bindings_unused` costs nothing at send, and
     `whatsapp_account_health` is every Meta flag that is worth saying and is
     not a certain refusal. */
  'wa_bindings_unused',
  'whatsapp_account_health',
] as const;
export const campaignScheduleIssueCodeSchema = z.enum(CAMPAIGN_SCHEDULE_ISSUE_VALUES);
export type CampaignScheduleIssueCode = z.infer<typeof campaignScheduleIssueCodeSchema>;

export const CAMPAIGN_ISSUE_SEVERITY_VALUES = ['blocker', 'advice'] as const;
export const campaignIssueSeveritySchema = z.enum(CAMPAIGN_ISSUE_SEVERITY_VALUES);
export type CampaignIssueSeverity = z.infer<typeof campaignIssueSeveritySchema>;

export const campaignScheduleIssueSchema = z
  .object({
    code: campaignScheduleIssueCodeSchema,
    severity: campaignIssueSeveritySchema,
    /** A whole sentence, safe to show a customer. */
    message: z.string(),
  })
  .strict();
export type CampaignScheduleIssue = z.infer<typeof campaignScheduleIssueSchema>;

/**
 * Everything the schedule step needs to decide, and everything the API needs to
 * refuse with. Returned by the readiness endpoint and echoed in the 422 body, so
 * a refusal and a preview are the same object.
 */
export const campaignScheduleReadinessSchema = z
  .object({
    canSchedule: z.boolean(),
    issues: z.array(campaignScheduleIssueSchema),
    reach: campaignReachSchema,
    sendGuard: sendGuardStatusSchema,
  })
  .strict();
export type CampaignScheduleReadiness = z.infer<typeof campaignScheduleReadinessSchema>;

/**
 * How a campaign's template pictures will travel, and — only where that means
 * hosting — how many of them have a public address to be served from.
 *
 * **A union on {@link TemplateImageDelivery}, and the rule is told the mode
 * rather than finding it out.** `@wend/shared` never reads the environment; the
 * API derives the mode once from `TEMPLATE_ASSET_PUBLIC_BASE_URL` and the
 * gathering in `campaign-readiness.ts` passes it here, beside the counts it
 * makes that mode mean something. The union is what keeps those two facts
 * together:
 *
 *   - `embedded` carries **no counts**, deliberately. An embedded picture needs
 *     no address, so there is nothing to count and nothing to refuse — and a
 *     shape with room for `hosted: 0` would invite a reading of "none of your
 *     images are hosted" on a deployment where that is simply how images travel.
 *     It also means the API reads nothing for it: the schedule path on an
 *     embedding deployment makes exactly the queries it made before hosting
 *     existed.
 *   - `hosted` carries **two counts, never a boolean**, because "this template
 *     has no images" and "this template's images cannot be hosted" are different
 *     facts with different consequences and a single flag would have to guess
 *     which one it meant. `referenced` counts **distinct assets an image block
 *     actually points at**, not asset rows on the template: an image uploaded
 *     and then removed from the document is not in the message and must not
 *     block a send. `hosted` counts how many of those same assets carry a
 *     `public_id`.
 *
 * The rejected alternative was the shape this replaced — two counts and no mode,
 * with hosting assumed. It could not express "this deployment embeds", so the
 * rule refused every template with a picture on the deployment sending real
 * campaigns.
 */
/**
 * The attachment half of the same question, in the same shape and for the same
 * reason — see {@link CampaignTemplateImages}, which this deliberately mirrors
 * rather than inventing a second vocabulary for.
 *
 * `total`, not `referenced`: nothing references an attachment. Every file on the
 * template travels with every message, so the cohort is the template's files.
 */
export type CampaignTemplateAttachments =
  | { readonly delivery: Extract<TemplateAttachmentDelivery, 'attached'> }
  | {
      readonly delivery: Extract<TemplateAttachmentDelivery, 'linked'>;
      /** Every attachment on the template. */
      readonly total: number;
      /** Of those, how many have a public id minted and can therefore be linked. */
      readonly published: number;
    };

export type CampaignTemplateImages =
  | { readonly delivery: Extract<TemplateImageDelivery, 'embedded'> }
  | {
      readonly delivery: Extract<TemplateImageDelivery, 'hosted'>;
      /** Distinct assets the template's image blocks reference. */
      readonly referenced: number;
      /** Of those, how many have a public id minted and can therefore be served. */
      readonly hosted: number;
    };

/** Everything {@link campaignScheduleIssues} needs, and nothing it cannot have. */
export interface CampaignScheduleContext {
  readonly status: CampaignStatus;
  readonly channel: Channel;
  /** Null when the template has been deleted out from under the campaign. */
  readonly template: { readonly type: TemplateType; readonly status: TemplateStatus } | null;
  readonly reach: CampaignReach;
  /**
   * How the template's pictures travel on this deployment, and — where they are
   * hosted — how many can actually be served.
   *
   * **Required, not optional, and that is the safety property.** An optional
   * member here would be forgotten by exactly one caller, that caller would
   * raise no issue, and the campaign it scheduled would arrive with broken
   * pictures — which is the failure this whole member exists to prevent. The
   * mode is required inside it for the matching reason: a caller cannot say
   * "hosted" without also saying what it counted.
   *
   * A WhatsApp campaign passes whatever the deployment's mode is, with
   * `referenced: 0` when that mode is `hosted`, which is honest: a WhatsApp
   * template has no blocks and therefore no image blocks.
   */
  readonly templateImages: CampaignTemplateImages;
  /**
   * How the template's files travel on this deployment, and — where they are
   * linked — how many can actually be reached.
   *
   * Required for the reason `templateImages` is, and the consequence of
   * forgetting is worse: a campaign scheduled with an unpublished picture
   * arrives with a broken image, and one scheduled with an unpublished file
   * arrives with the file simply missing and nothing on screen to say so.
   *
   * A WhatsApp campaign passes the deployment's mode with `total: 0`, which is
   * honest: a WhatsApp template's media header is `wa_header_media` and is never
   * an attachment.
   */
  readonly templateAttachments: CampaignTemplateAttachments;
  /**
   * The sending account, as {@link import('./integration.js').sesReadiness}
   * derived it. Null when no SES integration is connected at all.
   */
  readonly integration: {
    readonly state: SesReadinessState;
    /**
     * `null` means `ses:GetAccount` was refused, so AWS never told us either
     * way — **treated as not-a-blocker**, exactly as `fromAddressVerified` is
     * below and for the same reason: a readiness screen that could not look
     * must not invent a refusal. `false` is AWS actually saying it has paused
     * the account, and that still blocks.
     */
    readonly sendingEnabled: boolean | null;
    /**
     * Whether `GetAccount` answered. When it did not, `state` falls back to
     * `sandboxed` as the conservative reading — so this flag is what keeps the
     * advice from asserting a sandbox nobody observed.
     */
    readonly accountVisible: boolean;
  } | null;
  /**
   * Whether `fromEmail` is covered by a verified identity on that account.
   * `null` means "not checked" — treated as not-a-blocker, because a readiness
   * screen that cannot reach AWS must not invent a refusal.
   */
  readonly fromAddressVerified: boolean | null;
  /**
   * What this campaign's WhatsApp template needs filled, re-derived from the
   * document inside the scheduling transaction.
   *
   * **Required, not optional, for the reason `templateImages` gives in its own
   * comment**: an optional member is forgotten by exactly one caller, that
   * caller raises no issue, and the campaign it schedules is the campaign this
   * member exists to stop — one whose `{{3}}` nothing fills, discovered by Meta,
   * one recipient at a time, on the tenant's own WABA.
   *
   * `null` for an **email** campaign and for a **WhatsApp campaign whose
   * template could not be read**. The rule tells them apart by `ctx.channel` and
   * raises nothing in the second case: a reader that could not look must not
   * invent a refusal, and `template_not_approved` already fires when the
   * template is gone.
   */
  readonly waTemplate: { readonly requiredPositions: readonly number[] } | null;
  /**
   * The tenant's Meta WhatsApp account, as a stored check saw it. `null` for an
   * email campaign, and required for the same reason `waTemplate` is.
   *
   * `{ connected: false }` is "there is no `meta_whatsapp` integration row at
   * all" — a blocker, because nothing can send. `{ connected: true, readiness:
   * null }` is "a row exists and no check has ever succeeded", which is
   * **advice**, on the `account_not_readable` precedent: a readiness screen that
   * could not look must not invent a refusal. One `null` covering both would
   * make the second refuse a schedule on an account that is probably fine.
   */
  readonly waAccount: {
    readonly connected: boolean;
    readonly readiness: MetaReadiness | null;
  } | null;
  /** The bindings stored on the campaign. Empty for an email campaign. */
  readonly waBindings: readonly WaVariableBinding[];
  readonly sendGuard: SendGuardStatus;
  /** The instant being scheduled for, and the zone it was chosen in. */
  readonly scheduledAt: string;
  readonly timezone: string;
  /** Injected so the function stays pure and testable. Defaults to now. */
  readonly now?: Date;
}

/**
 * Everything wrong with scheduling this campaign, blockers and advice together.
 *
 * **Lives in `@wend/shared` for the same reason `sesReadiness` does.** The API
 * decides whether to allow the transition; the wizard decides whether to enable
 * the button and what to say beside it. Two implementations of "can this send"
 * drift, and the way they drift is that one of them stops checking the template
 * status — which is how a half-built email reaches a thousand strangers.
 *
 * It is a pure function of values both sides already hold, with no I/O.
 */
export function campaignScheduleIssues(ctx: CampaignScheduleContext): CampaignScheduleIssue[] {
  const issues: CampaignScheduleIssue[] = [];
  const now = ctx.now ?? new Date();

  if (ctx.status !== 'draft') {
    issues.push({
      code: 'campaign_not_draft',
      severity: 'blocker',
      message:
        ctx.status === 'scheduled'
          ? 'This campaign is already scheduled. Cancel it first if you want to change the time.'
          : `A campaign can only be scheduled from draft, and this one is ${ctx.status}.`,
    });
  }

  if (ctx.template === null) {
    issues.push({
      code: 'template_not_approved',
      severity: 'blocker',
      message: 'This campaign has no template. Choose one before scheduling.',
    });
  } else {
    if (ctx.template.status !== 'approved') {
      issues.push({
        code: 'template_not_approved',
        severity: 'blocker',
        message: `The template is ${ctx.template.status}. Only an approved template can be sent — open it and approve it first.`,
      });
    }
    if (ctx.template.type !== ctx.channel) {
      issues.push({
        code: 'template_channel_mismatch',
        severity: 'blocker',
        message: `This is a ${ctx.channel} campaign and the template is a ${ctx.template.type} template. They have to match.`,
      });
    }
  }

  if (ctx.reach.reachable === 0) {
    issues.push({
      code: 'no_reachable_recipients',
      severity: 'blocker',
      // Names both numbers, because "0 recipients" on a segment of 1,278 people
      // reads as a bug until you know the channel is what filters it.
      message:
        ctx.reach.segmentUniqueRecipients === 0
          ? 'This segment has nobody in it, so there is nobody to send to.'
          : `Nobody in this segment can be reached by ${ctx.reach.channel}. It holds ${ctx.reach.segmentUniqueRecipients} people, and none of them has ${ctx.reach.channel === 'email' ? 'an email address' : 'a phone number'}.`,
    });
  }

  /*
   * FAIL CLOSED ON IMAGES THAT CANNOT BE HOSTED — ON A DEPLOYMENT THAT HOSTS.
   *
   * **Conditional, and the condition is the point.** Where
   * `TEMPLATE_ASSET_PUBLIC_BASE_URL` is set, every image in an email is served
   * from a public URL rather than carried inside the message — see
   * `imageBlockSchema` in `template.js` for why, and `GMAIL_CLIP_BYTES` for the
   * arithmetic that argues for it. There an image with no public address does
   * not degrade: it arrives as a broken picture in every inbox, in a message the
   * recipient already did not ask for.
   *
   * Where it is not set, images travel inside the message as `cid:` parts, as
   * they did for the first 1,116 real messages, and an image needs no address at
   * all — so this rule has nothing to say and says nothing. As first written it
   * fired regardless of mode, which made every template with a picture
   * unschedulable on the one deployment sending real campaigns, where no image
   * host exists. SLICES' rule for exactly that: a change that would block a send
   * ships behind configuration. The rule does not read that configuration; it
   * is told the mode in `ctx.templateImages.delivery`.
   *
   * WHY A BLOCKER AND NOT ADVICE. Advice is for a cost somebody may reasonably
   * accept — quiet hours, a sandboxed account, a missing postal address on an
   * Indian list. Nobody reasonably accepts sending a broken image to a thousand
   * people, and there is no version of this the sender learns about in time from
   * a warning they clicked past.
   *
   * WHY AT SCHEDULE TIME AND NOT AT SEND TIME. At send time the campaign is
   * already running, a partial send has already gone out, and the refusal lands
   * as a failed job rather than as a sentence next to the button somebody just
   * pressed. Schedule time is the last moment at which refusing costs nothing
   * and the person who can fix it is the person reading the message. The send
   * path keeps its own refusal — `assertImageIsHosted`, and
   * `templateAssetPublicUrl` returning null rather than a broken string — as a
   * later lock, not as the first.
   *
   * WHAT THIS RULE CANNOT SEE: a campaign already scheduled, sending, paused or
   * holding between waves when the deployment switched from embedding to
   * hosting. It was checked under `embedded` and is never checked again, and a
   * refusal at send time would finish it as `failed`, which is terminal. So the
   * send runner publishes such a campaign's pictures itself before its next run
   * (`publishBeforeSend`, with the same function a template save uses) and
   * refuses it, naming the picture, only when publishing fails. This rule stays
   * a blocker for everything scheduled from then on.
   *
   * WHY IT DOES NOT FIRE FOR NO REASON: `referenced` counts image blocks, so a
   * template with no images raises nothing at all even where images are
   * hosted; and a deployment with no image host — every developer's, and the
   * live one today — never reaches the count.
   *
   * WHAT THE PERSON READING IT CAN DO. The images this catches are, almost
   * always, pictures uploaded before hosting was switched on: they have no
   * public id because none was minted then. Saving the template mints one for
   * each (`publishInlineImages` in the API), and the editor offers Save for
   * exactly that. The sentence is `templateImagesUnpublishedMessage`, shared
   * with the editor, and it deliberately does not name an environment variable:
   * this branch only runs where that variable is already set.
   */
  if (ctx.templateImages.delivery === 'hosted') {
    const unhostedImages = ctx.templateImages.referenced - ctx.templateImages.hosted;
    if (unhostedImages > 0) {
      issues.push({
        code: 'template_images_not_hostable',
        severity: 'blocker',
        message: `${templateImagesUnpublishedMessage(unhostedImages, ctx.templateImages.referenced)} Nothing can be scheduled until then.`,
      });
    }
  }

  /*
   * The same rule for files, and it is a blocker for a stronger reason than the
   * picture one.
   *
   * A picture with no address arrives broken: wrong, visible, and the reader
   * knows something was there. A file with no address is dropped from a message
   * that never mentions it — the reader is told nothing, and the sender learns
   * nothing either. The send path's `assertAttachmentIsLinked` is the later
   * lock; this is the one that fires while somebody is still at a screen.
   *
   * It cannot see a campaign already scheduled when the deployment switched
   * modes, exactly as the picture rule cannot, and the answer is the same:
   * `publishBeforeSend` publishes that campaign's files before its next run and
   * refuses it by name only when publishing fails.
   */
  if (ctx.templateAttachments.delivery === 'linked') {
    const unpublished = ctx.templateAttachments.total - ctx.templateAttachments.published;
    if (unpublished > 0) {
      issues.push({
        code: 'template_attachments_not_linkable',
        severity: 'blocker',
        message: `${templateAttachmentsUnpublishedMessage(unpublished, ctx.templateAttachments.total)} Nothing can be scheduled until then.`,
      });
    }
  }

  /*
   * THE SENDING ACCOUNT, AND IT IS THE ONE PLACE THIS RULE IS CHANNEL-AWARE.
   *
   * Structured as `if (email) { …the block that has sent 1,100+ real messages,
   * byte for byte… } else { …WhatsApp… }` rather than as two sets of conditions
   * woven together, because the email path must be unchanged by this slice and
   * "unchanged" should be visible in a diff rather than argued for in a review.
   *
   * A WhatsApp campaign reads `waAccount` and NEVER `integration` or
   * `fromAddressVerified`, and that is not tidiness. `isFromAddressVerified`
   * takes a string; on a WhatsApp campaign there is no address, and the obvious
   * `campaign.fromEmail ?? ''` hands it an empty string, from which it finds no
   * `@`, derives an empty domain, matches no identity and returns **false** —
   * which is a BLOCKER reading "AWS will refuse the message". Every WhatsApp
   * campaign in the product would be unschedulable, for a reason about an
   * account that has nothing to do with the send, and every type would check.
   * So the SES members are reached only inside this first branch, and the API
   * passes `null` for them on the other one.
   */
  if (ctx.channel === 'email') {
    if (ctx.integration === null) {
      issues.push({
        code: 'integration_not_ready',
        severity: 'blocker',
        message: 'No sending account is connected. Connect your Amazon SES account before scheduling.',
      });
    } else {
      if (ctx.integration.state === 'no_verified_identity') {
        issues.push({
          code: 'integration_not_ready',
          severity: 'blocker',
          message:
            'Your SES account has no identity that can send — nothing is verified, or AWS has paused the one that is. There is nothing to send from.',
        });
      }
      /*
       * `account_not_readable` is ADVICE, and deliberately NOT a blocker.
       *
       * The key authenticates; AWS simply declined the two read calls. Wend uses
       * them to populate a panel and to pre-check the from address — nothing a
       * send depends on, because AWS enforces identity verification itself at
       * send time and will refuse the message if the sender is not verified.
       *
       * Refusing to schedule here would stop a tenant whose account is live and
       * correctly configured from sending at all, over a permission that exists
       * for Wend's convenience. The warning is worth giving; the refusal is not
       * ours to make.
       */
      if (ctx.integration.state === 'account_not_readable') {
        issues.push({
          code: 'sending_account_sandboxed',
          severity: 'advice',
          message:
            'This key cannot read your SES account, so Wend cannot confirm there is a verified sender or whether the account has left the AWS sandbox. Sending still works — but if either is wrong, AWS will refuse the messages and Wend will not have warned you first.',
        });
      }
      // `=== false`, never `!`. Null is "AWS never told us", and `!null` is
      // `true`, which would refuse to schedule on a perfectly healthy account
      // because one optional read-only permission was missing from the key.
      if (ctx.integration.sendingEnabled === false) {
        issues.push({
          code: 'integration_not_ready',
          severity: 'blocker',
          message: 'AWS has paused sending on this account. Nothing will be delivered until that is resolved.',
        });
      }
      if (ctx.integration.state === 'sandboxed') {
        issues.push({
          code: 'sending_account_sandboxed',
          // Advice, not a refusal: a sandboxed account can send to verified
          // addresses, which is exactly what a first campaign does. Discovering
          // the sandbox after a campaign reaches nobody is the failure slice 3
          // exists to prevent, so it is said here, loudly, every time.
          severity: 'advice',
          // Two messages, because only one of them is a fact. `state` is
          // `sandboxed` both when AWS said so and when AWS was never asked, and
          // telling somebody their account IS sandboxed when we could not look
          // is the same class of false sentence as a rate over an empty
          // denominator. The advice is worth giving either way — the consequence
          // is identical if it turns out to be true — so what changes is the
          // claim, not the warning.
          message: ctx.integration.accountVisible
            ? 'Your SES account is still in the AWS sandbox: about 200 messages a day, a capped rate, and delivery only to addresses you have verified with AWS. Everyone else will bounce.'
            : 'Wend cannot tell whether this SES account has left the AWS sandbox, because the key cannot call GetAccount. If it has not, this campaign will deliver only to addresses you have verified with AWS and everyone else will bounce.',
        });
      }
      if (ctx.fromAddressVerified === false) {
        issues.push({
          code: 'from_address_not_verified',
          severity: 'blocker',
          message:
            'The from address is not covered by a verified identity on your SES account. AWS will refuse the message.',
        });
      }
    }
  } else if (ctx.channel === 'whatsapp' && (ctx.waAccount === null || !ctx.waAccount.connected)) {
    issues.push({
      code: 'whatsapp_account_not_connected',
      severity: 'blocker',
      message: 'No WhatsApp account is connected. Connect your Meta WhatsApp account before scheduling.',
    });
  } else if (ctx.channel === 'whatsapp' && ctx.waAccount !== null && ctx.waAccount.readiness === null) {
    /*
     * A row exists and no check has ever succeeded. ADVICE and never a blocker,
     * on the `account_not_readable` precedent one branch up: a readiness screen
     * that could not look must not invent a refusal. The account is probably
     * fine; what is missing is our knowledge of it, and that is what the
     * sentence says.
     */
    issues.push({
      code: 'whatsapp_account_health',
      severity: 'advice',
      message:
        'Wend has never successfully checked this WhatsApp account, so it cannot confirm the number can send. Test the connection on Integrations.',
    });
  } else if (ctx.channel === 'whatsapp' && ctx.waAccount !== null && ctx.waAccount.readiness !== null) {
    /*
     * EVERY BRANCH ABOVE NAMES THE CHANNEL, and none of them is a bare `else`.
     * `CHANNEL_VALUES` is `['email', 'whatsapp']` today, so a chain that fell
     * through to WhatsApp's account refusals was correct — and slice 54 adds
     * `sms`, at which point every SMS campaign would be blocked by "No WhatsApp
     * account is connected", with no type error and no failing test to say so.
     * A channel this gate has not been taught raises nothing here.
     */
    const readiness = ctx.waAccount.readiness;
    /*
     * EVERY FLAG THAT IS SET IS RAISED, not only the headline `state` —
     * `metaReadiness`'s own comment instructs exactly that, and the reason is
     * that its state is a priority ordering: a number that is both unverified
     * and on a token expiring tomorrow reports `number_not_verified` and the
     * token would go unmentioned until the day it broke.
     */
    if (!readiness.numberVerified) {
      issues.push({
        code: 'whatsapp_number_not_verified',
        severity: 'blocker',
        // A blocker because a number that cannot send makes every other warning
        // academic — the same ordering `metaReadiness` already states.
        /* The sentence `META_READINESS_DISPLAY.number_not_verified` already
           shows on Integrations. One product says one thing about one Meta
           state, and `numberVerified` is false for an EXPIRED verification as
           well as a missing one — which "has not verified" would misdescribe. */
        message:
          'Meta has not verified this phone number, so it cannot send. Finish verification in WhatsApp Manager, then test again.',
      });
    }
    if (readiness.tokenExpiry === 'expired') {
      issues.push({
        code: 'whatsapp_token_expired',
        severity: 'blocker',
        // Certain refusal at Meta, so it is refused here, where it costs a
        // sentence rather than a campaign that fails per recipient.
        message:
          'The token for this WhatsApp account has expired. Meta will refuse every message until it is replaced.',
      });
    }
    if (readiness.qualityFlagged) {
      issues.push({
        code: 'whatsapp_account_health',
        // Advice: a flagged number can still send, and a sender may reasonably
        // decide to accept slower delivery rather than wait for a rating to
        // recover on Meta's schedule.
        severity: 'advice',
        /* Reused verbatim from `META_READINESS_DISPLAY.quality_flagged`. The
           sentence this replaced claimed slow or missing delivery, which Meta
           does not document — and this repository says twice, in `chart.ts` and
           `ActivityCharts.tsx`, that the rating FOLLOWS undelivered messages
           rather than causing them. */
        message:
          'Meta has rated this number’s quality red. Its messaging limit is likely to be cut, and it can be paused outright — send less, and only to people expecting it.',
      });
    }
    if (readiness.wabaPendingReview) {
      issues.push({
        code: 'whatsapp_account_health',
        severity: 'advice',
        /* `META_READINESS_DISPLAY.waba_pending_review`, verbatim: templates CAN
           be submitted during review, which the sentence this replaced denied. */
        message:
          'Meta is still reviewing this WhatsApp Business Account. Templates can be submitted, but sending is restricted until the review finishes.',
      });
    }
    if (readiness.businessUnverified) {
      issues.push({
        code: 'whatsapp_account_health',
        severity: 'advice',
        /* The consequence `integration.ts` records for this same boolean:
           template volume, not a daily people cap. */
        message:
          'Business verification is incomplete at Meta, which caps how many templates this account can use.',
      });
    }
    if (readiness.tokenExpiry === 'expiring') {
      issues.push({
        code: 'whatsapp_account_health',
        severity: 'advice',
        message:
          'This WhatsApp token expires within 14 days. Replace it before it does, or sending stops without warning.',
      });
    }
  }

  /*
   * THE BINDINGS, AND WHY THIS IS A SCHEDULE RULE RATHER THAN A SHAPE ONE.
   *
   * "Which positions does this template need" needs the `templates` row, so
   * `waBindingListSchema` cannot ask it — that schema refuses a duplicated
   * position and an unknown field, which need nothing but the request. This
   * needs the document, and it is re-derived **inside the scheduling
   * transaction** rather than taken from whatever the composer last saw: a
   * template edited under a draft can grow `{{3}}` after the personalisation
   * panel rendered, and the stale tab would otherwise schedule a campaign that
   * cannot fill it.
   *
   * Gated on `waTemplate !== null`, which on a WhatsApp campaign means the
   * document could not be read. Nothing is raised then, for the reason the
   * account branch gives: a reader that could not look invents no refusal, and
   * `template_not_approved` already covers a template that is gone.
   */
  if (ctx.channel === 'whatsapp' && ctx.waTemplate !== null) {
    const { unbound, unused } = waBindingState(ctx.waTemplate.requiredPositions, ctx.waBindings);

    if (unbound.length > 0) {
      issues.push({
        code: 'wa_bindings_incomplete',
        severity: 'blocker',
        message:
          unbound.length === 1
            ? `This WhatsApp template needs ${formatWaPositions(unbound)}, and this campaign has no field for it. Add one before scheduling.`
            : `This WhatsApp template needs ${formatWaPositions(unbound)}, and this campaign has no field for them. Add one for each before scheduling.`,
      });
    }

    if (unused.length > 0) {
      issues.push({
        code: 'wa_bindings_unused',
        // ADVICE. A leftover row costs nothing at send — `waValuesForRecipient`
        // resolves it and `assembleWhatsAppMessage` never asks for it — and
        // refusing a schedule over one would turn editing a template into a
        // campaign that cannot go out.
        severity: 'advice',
        message:
          unused.length === 1
            ? `${formatWaPositions(unused)} has a field and this template no longer uses it. It will be ignored.`
            : `${formatWaPositions(unused)} have fields and this template no longer uses them. They will be ignored.`,
      });
    }
  }

  const when = new Date(Date.parse(ctx.scheduledAt));
  if (!Number.isNaN(when.getTime())) {
    if (when.getTime() < now.getTime() - SCHEDULE_PAST_GRACE_MINUTES * 60_000) {
      issues.push({
        code: 'scheduled_in_past',
        severity: 'blocker',
        message: 'That time has already passed. Pick a time from now onwards.',
      });
    }
    if (isQuietHour(when, ctx.timezone)) {
      issues.push({
        code: 'quiet_hours',
        // Warns, never blocks — SLICES is explicit. A rule that refuses the
        // common case to prevent the rare one gets worked around, not heeded.
        severity: 'advice',
        message: `That lands between ${QUIET_HOURS_START_HOUR - 12}pm and ${QUIET_HOURS_END_HOUR}am in ${ctx.timezone}. Marketing email delivered overnight draws more spam complaints, and complaints are what get a sending account suspended. You can still send it.`,
      });
    }
  }

  /*
   * The guard's own advice. Never a blocker: an empty allow-list must produce a
   * campaign that materialises and skips everyone — which is what proves the
   * guard works — rather than a campaign that cannot be scheduled at all.
   *
   * Both branches are gated on `enforced`, and that is not defensive tidying.
   * The allow-list keeps its entries when the guard is lifted — leaving them is
   * what makes putting the guard back a one-line revert — so an unguarded
   * deployment still has an `allowlistSize`, and one that had emptied the list
   * BEFORE lifting would otherwise be told "this campaign will deliver nothing"
   * about a process that is about to deliver to everybody. That is the worst
   * possible direction for this sentence to be wrong in.
   */
  if (!ctx.sendGuard.enforced) {
    issues.push({
      code: 'send_allowlist_empty',
      severity: 'advice',
      message:
        'The send guard is lifted in this environment, so the allow-list is not being applied: every reachable person in this campaign will actually receive it.',
    });
  } else if (ctx.sendGuard.allowlistSize === 0) {
    issues.push({
      code: 'send_allowlist_empty',
      severity: 'advice',
      message:
        'The send allow-list is empty, so this campaign will deliver nothing. Every recipient will be skipped and told why. This is the send guard, not a fault.',
    });
  } else if (ctx.sendGuard.deliverable !== null && ctx.sendGuard.deliverable < ctx.reach.reachable) {
    issues.push({
      code: 'send_guard_restricts_delivery',
      severity: 'advice',
      message: `Delivery is restricted to the send allow-list: of the ${ctx.reach.reachable} people this campaign reaches, ${ctx.sendGuard.deliverable} will receive it and the rest will be skipped.`,
    });
  }

  return issues;
}

/** The issues that actually refuse. Every API refusal path filters through this. */
export function campaignScheduleBlockers(ctx: CampaignScheduleContext): CampaignScheduleIssue[] {
  return campaignScheduleIssues(ctx).filter((issue) => issue.severity === 'blocker');
}

export function canScheduleCampaign(ctx: CampaignScheduleContext): boolean {
  return campaignScheduleBlockers(ctx).length === 0;
}

/* -------------------------------------------------------------------------- */
/* Test send                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Send one message, to one address, to see what it looks like.
 *
 * **There is no `segmentId` and no `campaignId`, and there must never be.** A
 * test send that can take an audience is not a test send, it is a send with a
 * friendlier name and no schedule, no recipient list and no progress screen. One
 * address, spelled out by the person asking for it.
 *
 * **The send guard applies in full.** A test send to an address that is not on
 * `SEND_ALLOWLIST` is refused, and refused the same way a campaign recipient is
 * — not in a test, not in a verification script, not "just once to check". This
 * is the endpoint most likely to be reached for by somebody in a hurry, which is
 * exactly why it gets no exemption.
 *
 * `sampleValues` reuses slice 4's preview substitution: a test send has no
 * contact, so `{{ name }}` has to come from somewhere and a visible sample is
 * more honest than an empty string.
 */
export const testSendRequestSchema = z
  .object({
    templateId: uuidSchema,
    /** The single destination. Checked against the send allow-list. */
    to: emailSchema,
    fromEmail: emailSchema,
    fromName: campaignFromNameSchema.optional().default(null),
    /** Overrides for `{{ variables }}`, as in the template preview. */
    sampleValues: z.record(z.string(), z.string().max(500)).optional(),
  })
  .strict();
export type TestSendRequest = z.infer<typeof testSendRequestSchema>;

/**
 * What a test send answers.
 *
 * A guard refusal is `{ sent: false }` with the decision, not an HTTP error: the
 * guard doing its job is a *result* to render, in the same panel, in the same
 * words a skipped recipient gets. An error envelope would make the most common
 * outcome in a development environment look like a fault.
 *
 * `messageId` is the provider's id and is deliberately absent from the failure
 * branch — nothing was handed to a provider, so there is nothing to identify.
 */
export const testSendResponseSchema = z.discriminatedUnion('sent', [
  z
    .object({
      sent: z.literal(true),
      /** The provider's message id, for tracing a real send. */
      messageId: z.string(),
      /**
       * Which transport delivered it. `stub` is surfaced, never hidden — the
       * slice 3 lesson: a mock mistaken for the real thing has already cost
       * this project a wasted verification.
       */
      transport: sesTransportSchema,
    })
    .strict(),
  z.object({ sent: z.literal(false), guard: sendGuardBlockedSchema }).strict(),
]);
export type TestSendResponse = z.infer<typeof testSendResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Unsubscribe                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The per-recipient unsubscribe token.
 *
 * Opaque, minted at send, substituted into `{{ unsubscribe_url }}` — the
 * placeholder slice 4 reserved. Only its SHA-256 hash is stored, exactly as the
 * set-password token is, so a database read cannot reconstruct a working link
 * for somebody else's recipient.
 *
 * **It never expires.** That is a deliberate difference from every other token
 * in this codebase, and the reason is the whole point of the feature: people
 * unsubscribe from a message they find months later, and a dead unsubscribe link
 * is precisely how a tenant's own AWS account gets suspended. An expiring
 * unsubscribe is worse than none, because it looks compliant.
 */
export const unsubscribeTokenSchema = z.string().trim().min(20).max(512);
export type UnsubscribeToken = z.infer<typeof unsubscribeTokenSchema>;

/** `/u/:token` — the public route. No session, no tenant header, no cookie. */
export const unsubscribeParamsSchema = z.object({ token: unsubscribeTokenSchema });
export type UnsubscribeParams = z.infer<typeof unsubscribeParamsSchema>;

/**
 * The `List-Unsubscribe-Post` header value, and the body a mail client posts.
 *
 * RFC 8058 one-click: the header goes on every message alongside
 * `List-Unsubscribe: <https://…/u/token>`, and a client honouring it POSTs this
 * exact form body to that URL.
 */
export const ONE_CLICK_UNSUBSCRIBE_POST_HEADER = 'List-Unsubscribe=One-Click';

/**
 * The one-click POST body.
 *
 * `.passthrough()` because the sender is a mail client rather than our own
 * frontend: Gmail, Outlook and Apple Mail are not obliged to send exactly what
 * we expect, and refusing an unsubscribe over a surplus field would be refusing
 * an unsubscribe. The field is optional for the same reason — our own
 * confirmation page POSTs the same route with an empty body.
 *
 * **Why a POST at all, and why GET must not act.** Mail scanners, link
 * previewers and corporate security appliances follow every URL in an email. A
 * GET that unsubscribes is a GET that unsubscribes people who never opened the
 * message. So `GET /u/:token` verifies the token and renders a confirmation —
 * {@link verifyUnsubscribeTokenResponseSchema} — and only the POST writes to
 * `suppressions`.
 */
export const oneClickUnsubscribeRequestSchema = z
  .object({ 'List-Unsubscribe': z.literal('One-Click').optional() })
  .passthrough();
export type OneClickUnsubscribeRequest = z.infer<typeof oneClickUnsubscribeRequestSchema>;

/**
 * What the confirmation page is told about a token.
 *
 * The address comes back **masked** (`p***@bank.in`, the API's own `maskEmail`).
 * This page is served to anyone holding the link — including every scanner that
 * followed it — so it shows enough for the recipient to recognise themselves and
 * not enough to harvest. `valid: false` carries nothing at all: an invalid token
 * must not be able to distinguish "never existed" from "already used".
 */
export const verifyUnsubscribeTokenResponseSchema = z
  .object({
    valid: z.boolean(),
    /** Masked, present only when valid. Never the whole address. */
    maskedEmail: z.string().optional(),
    /** True when this address is already suppressed, so the page can say so. */
    alreadySuppressed: z.boolean().optional(),
  })
  .strict();
export type VerifyUnsubscribeTokenResponse = z.infer<typeof verifyUnsubscribeTokenResponseSchema>;

/**
 * The answer to an accepted unsubscribe.
 *
 * `success: true` for an address that was already suppressed as well as for one
 * suppressed just now — the operation is idempotent, and a second click must not
 * produce an error page for somebody who is trying to leave. `reason` is always
 * `unsubscribe` here; the other {@link suppressionReasonSchema} members are
 * written by bounce and complaint handling in slice 6.
 */
export const unsubscribeResponseSchema = z
  .object({
    success: z.literal(true),
    reason: suppressionReasonSchema,
    /** False when the address was already on the suppression list. */
    suppressedNow: z.boolean(),
  })
  .strict();
export type UnsubscribeResponse = z.infer<typeof unsubscribeResponseSchema>;

/**
 * `GET /campaigns/:id/list-safety?restCritical=true` — slice 59. The schedule
 * step asks for the warm-up preview as it WOULD be with "rest people at
 * Critical" on, so the batches it promises are sized for the people actually
 * left, as the committed plan is. A query string because a GET has no body;
 * `'true'` or absent, nothing else.
 */
export const campaignListSafetyQuerySchema = z
  .object({
    restCritical: z.literal('true').optional(),
    /** Slice 61: the preview with this share held back. A query string, so digits. */
    holdoutPercent: z.enum(['5', '10', '20']).optional(),
  })
  .strict();
export type CampaignListSafetyQuery = z.infer<typeof campaignListSafetyQuerySchema>;
